import { Component, Suspense, lazy, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ClipboardList, History, PackageSearch, Printer, RefreshCw, Route, TimerReset } from 'lucide-react';
import EmptyState from '@/components/common/EmptyState.jsx';
import StatusChip from '@/components/common/StatusChip.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import Select from '@/components/ui/select.jsx';
import Switch from '@/components/ui/switch.jsx';
import Textarea from '@/components/ui/textarea.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { OperationalCombobox } from '@/pages/Fojas/components/OperationalCombobox.jsx';
import { useOperationalShortcuts } from '@/hooks/useOperationalShortcuts.js';
import VehicleLoadGauge from '@/pages/Repartos/VehicleLoadGauge.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { hasPermission } from '@/utils/permissions.js';
import { showError } from '@/utils/alerts.js';
import { useClient, useQuery } from 'urql';
import { CHOFERES_LIST } from '@/graphql/choferes.gql.js';
import { LINEAS_QUERY } from '@/graphql/lineas.gql.js';
import { fetchVehiculos } from '@/pages/Administracion/Flota/services/flota.service.js';
import {
  emitFoja,
  getFojaDetail,
  listFojaLineas,
  listGuiasElegibles,
  prevalidateEmitFoja,
} from '@/services/fojas.js';
import { printFojaPdf } from '@/modules/fojas/services/printFoja.js';

const QuickCreateChoferModal = lazy(() => import('@/pages/Fojas/components/QuickCreateChoferModal.jsx'));
const QuickCreateVehiculoModal = lazy(() => import('@/pages/Fojas/components/QuickCreateVehiculoModal.jsx'));

const STATE_VARIANTS = {
  borrador: 'outline',
  cerrada: 'warning',
  despachada_confirmada: 'default',
  recibida: 'default',
  recibida_parcial: 'warning',
};

const RECEPCION_MODOS = [
  { value: 'manual_por_guia', label: 'Manual por guía' },
  { value: 'marcar_todo', label: 'Marcar todo' },
  { value: 'qr_obligatorio', label: 'QR obligatorio' },
];

const SCAN_FEEDBACK_STYLES = {
  success: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-50',
  warning: 'border-amber-400/40 bg-amber-500/10 text-amber-50',
  danger: 'border-red-400/40 bg-red-500/10 text-red-100',
};

const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

function ShortcutChip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[rgba(var(--border),0.42)] bg-[rgba(var(--subpanel),0.76)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.7)]">
      {children}
    </span>
  );
}

const getSucursalInfo = (auth) => {
  const sucursal = auth?.sucursalActiva || auth?.sucursal || null;
  return {
    id: sucursal?.id || sucursal?.sucursal_id || null,
    nombre: sucursal?.nombre || sucursal?.descripcion || 'Sucursal activa',
  };
};

const getEmpresaId = (auth) => {
  const authEmpresaId =
    auth?.user?.empresaId
    || auth?.user?.empresa_id
    || auth?.user?.empresa?.id
    || auth?.tenant?.empresaId
    || auth?.tenant?.empresa_id
    || auth?.tenant?.empresa?.id
    || auth?.tenant?.id
    || auth?.sucursalActiva?.empresa_id
    || auth?.sucursalActiva?.empresaId
    || auth?.sucursal?.empresa_id
    || auth?.sucursal?.empresaId
    || null;

  if (authEmpresaId) return authEmpresaId;
  if (typeof window === 'undefined') return null;

  try {
    const storedTenant = window.localStorage.getItem('tenant');
    if (storedTenant) {
      const parsedTenant = JSON.parse(storedTenant);
      const tenantEmpresaId =
        parsedTenant?.empresaId
        || parsedTenant?.empresa_id
        || parsedTenant?.empresa?.id
        || parsedTenant?.id
        || null;
      if (tenantEmpresaId) return tenantEmpresaId;
    }

    const storedUser = window.localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      return (
        parsedUser?.empresaId
        || parsedUser?.empresa_id
        || parsedUser?.empresa?.id
        || parsedUser?.sucursalActiva?.empresa_id
        || parsedUser?.sucursalActiva?.empresaId
        || parsedUser?.sucursal?.empresa_id
        || parsedUser?.sucursal?.empresaId
        || null
      );
    }
  } catch {
    return null;
  }

  return null;
};

const getLineaSummaryKey = (item) => String(item?.linea_id || item?.linea || '');
const normalizeGuideLookup = (value) => String(value ?? '').trim().replace(/\s+/g, '').toUpperCase();
const extractGuiaIdFromScan = (value) => {
  const match = String(value || '').trim().match(/^SINAMON:GUIA:(\d+):/i);
  return match ? Number(match[1]) : null;
};
const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
const normalizeDbColumnName = (columnName) => String(columnName || '').replace(/_/g, ' ').trim();
const getEmitFailureMessage = (error, fallback = 'No se pudo emitir la foja.') => {
  const payload = error?.payload || error?.response?.data || null;
  const rawMessage = payload?.message || payload?.error || error?.message || '';
  const unknownColumnMatch = rawMessage.match(/Unknown column '([^']+)' in 'INSERT INTO'/i);

  if (unknownColumnMatch) {
    const fieldName = normalizeDbColumnName(unknownColumnMatch[1]);
    return `No se pudo emitir la foja porque el servidor no reconoce el campo ${fieldName} al guardar la emisión.`;
  }

  return rawMessage || fallback;
};
const getGuideSelectionLabel = (count) => `${formatNumber(count, 0)} guia${count === 1 ? '' : 's'} seleccionada${count === 1 ? '' : 's'}`;
const compactControlClassName = 'h-9 rounded-xl px-3 py-1.5 text-[13px] leading-5';
const compactTextareaClassName = 'min-h-[76px] rounded-xl px-3 py-2 text-[13px] leading-5';
const compactFieldLabelClassName = 'block text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]';
const compactFieldHeaderClassName = 'mb-0.5 flex h-5 min-h-5 items-center justify-between gap-2';
const compactFieldActionClassName = 'h-5 shrink-0 whitespace-nowrap px-1.5 text-[11px] font-medium leading-none text-[rgba(var(--fg),0.72)]';
const formatCurrencyShort = (value) => `$${formatNumber(value, 2)}`;
const DISPLAY_FALLBACK = 'Sin dato';
const PLACEHOLDER_TEXTS = new Set(['sin dato', 'sin datos', 'sin dato.', 'sin datos.', '-', 'n/a', 'na']);
const ENTITY_TEXT_KEYS = ['nombre', 'razon_social', 'razonSocial', 'descripcion', 'descripcion_corta', 'descripcionCorta', 'nombre_fantasia', 'nombreFantasia', 'apellido_nombre', 'apellidoNombre', 'label', 'title'];

function normalizeEntityText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (PLACEHOLDER_TEXTS.has(trimmed.toLowerCase())) return '';
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return normalizeEntityText(JSON.parse(trimmed));
      } catch {
        // keep the raw string when it is not valid JSON
      }
    }
    return trimmed;
  }
  if (typeof value === 'number' || typeof value === 'bigint') return String(value).trim();
  if (Array.isArray(value)) return value.map(normalizeEntityText).find(Boolean) || '';
  if (typeof value === 'object') {
    for (const key of ENTITY_TEXT_KEYS) {
      const nestedValue = normalizeEntityText(value?.[key]);
      if (nestedValue) return nestedValue;
    }
    return '';
  }
  return String(value).trim();
}

function hasMeaningfulText(value) {
  return normalizeEntityText(value) !== '';
}

function pickFirstMeaningful(...values) {
  return values.map(normalizeEntityText).find(Boolean) || '';
}

function buildDisplayPayload(primary, secondary = '') {
  return {
    primary: primary || DISPLAY_FALLBACK,
    secondary,
    title: [primary || DISPLAY_FALLBACK, secondary].filter(Boolean).join(' / '),
  };
}

function normalizeNumericValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'object') {
    return normalizeNumericValue(
      value?.valor_declarado
      ?? value?.valorDeclarado
      ?? value?.monto
      ?? value?.importe
      ?? value?.total
      ?? value?.valor
      ?? value?.amount,
    );
  }

  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(/,/g, '.')
    .replace(/[^\d.-]/g, '');
  if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function getRemitenteDisplay(row) {
  const primary = pickFirstMeaningful(
    row?.remitente_nombre,
    row?.remitente_razon_social,
    row?.nombre_remitente,
    row?.remitente,
    row?.remitente?.razon_social,
    row?.remitente?.razonSocial,
    row?.remitente?.nombre,
    row?.remitente?.descripcion,
    row?.remitente?.label,
    row?.remitente_snapshot,
  );

  return buildDisplayPayload(primary);
}

function getClienteDisplay(row) {
  const primary = pickFirstMeaningful(
    row?.cliente_nombre,
    row?.cliente_razon_social,
    row?.nombre_cliente,
    row?.cliente_nombre_comercial,
    row?.cliente?.razon_social,
    row?.cliente?.razonSocial,
    row?.cliente?.nombre,
    row?.cliente?.descripcion,
    row?.cliente?.label,
    row?.cliente?.nombre_fantasia,
    row?.pagador_nombre,
    row?.pagador_razon_social,
    row?.pagador?.nombre,
    row?.pagador?.razon_social,
    row?.pagador?.descripcion,
    row?.cliente_pagador_snapshot,
  );

  return buildDisplayPayload(primary);
}

function getDestinatarioDisplay(row) {
  const primary = pickFirstMeaningful(
    row?.destinatario_nombre,
    row?.destinatario_razon_social,
    row?.nombre_destinatario,
    row?.destinatario,
    row?.destinatario?.razon_social,
    row?.destinatario?.razonSocial,
    row?.destinatario?.nombre,
    row?.destinatario?.descripcion,
    row?.destinatario?.label,
    row?.destinatario_snapshot,
  );

  return buildDisplayPayload(primary);
}

function getDestinoDisplay(row) {
  const primary = pickFirstMeaningful(
    row?.localidad_destino_nombre,
    row?.localidad_destino,
    row?.localidad_nombre,
    row?.destino_nombre,
    row?.destino,
    row?.localidad_destino?.nombre,
    row?.localidad_destino?.descripcion,
    row?.destino?.nombre,
    row?.destino?.descripcion,
    row?.sucursal_destino_nombre,
    row?.sucursal_destino?.nombre,
    row?.sucursal_via_nombre,
    row?.sucursal_via?.nombre,
  );
  const secondaryRaw = pickFirstMeaningful(
    row?.localidad_destino_secundaria_nombre,
    row?.localidad_secundaria_nombre,
    row?.sucursal_via_nombre,
    row?.sucursal_via?.nombre,
    row?.sucursal_destino_nombre,
    row?.sucursal_destino?.nombre,
  );
  const secondary = secondaryRaw && secondaryRaw !== primary ? secondaryRaw : '';

  return buildDisplayPayload(primary, secondary);
}

function getRemitoDisplay(row) {
  const primary = pickFirstMeaningful(
    row?.remito_numero,
    row?.numero_remito,
    row?.remito?.numero,
    row?.remito?.nro,
    row?.remito?.codigo,
    row?.remito,
  );

  return {
    primary: primary || DISPLAY_FALLBACK,
    title: primary || DISPLAY_FALLBACK,
  };
}

function getValorDeclaradoDisplay(row) {
  const numericValue = normalizeNumericValue(
    row?.valor_declarado
    ?? row?.vd
    ?? row?.valorDeclarado
    ?? row?.valor_declarado_importe
    ?? row?.valor_decl
    ?? row?.total_valor_declarado
    ?? row?.total,
  );

  return {
    raw: numericValue,
    primary: numericValue === null ? DISPLAY_FALLBACK : formatCurrencyShort(numericValue),
    title: numericValue === null ? DISPLAY_FALLBACK : formatCurrencyShort(numericValue),
  };
}

function useTouchLikeMedia() {
  const [touchLike, setTouchLike] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const query = window.matchMedia('(hover: none), (pointer: coarse)');
    const update = () => setTouchLike(Boolean(query.matches));

    update();
    query.addEventListener?.('change', update);

    return () => {
      query.removeEventListener?.('change', update);
    };
  }, []);

  return touchLike;
}

function FojaOperationalHeader({
  estadoFoja,
  sucursalNombre,
  activeTotals,
  limitStatus,
  isSubmitting,
  isFojaEmitted,
  primaryAction,
  onRefreshAction,
  onPrintAction,
  onEmitValorizada,
  onEditSelection,
}) {
  const totals = activeTotals || {};
  const status = limitStatus || { label: 'Capacidad no informada', tone: 'neutral' };
  const estadoLabel = estadoFoja === 'sin_crear'
    ? 'BORRADOR · AÚN NO EMITIDA'
    : String(estadoFoja || 'borrador').replaceAll('_', ' ');

  return (
    <section className="sticky top-2 z-30 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.96)] px-2.5 py-2 shadow-[0_18px_34px_rgba(var(--shadow),0.14)]">
      <div className="flex flex-col gap-2.5 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.56)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.7)]">
              <ClipboardList size={12} />
              Emision de foja
            </div>
            <h1 className="text-base font-semibold leading-none sm:text-lg">Foja de ruta intersucursal</h1>
            <Badge variant={STATE_VARIANTS[estadoFoja] || 'outline'} className="px-2 py-0.5 text-[9px] tracking-[0.14em]">
              {estadoLabel}
            </Badge>
            <StatusChip label={status.label} tone={status.tone} />
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[rgba(var(--fg),0.68)]">
            <span className="rounded-full border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.5)] px-2 py-0.5">Sucursal {sucursalNombre}</span>
            <span className="rounded-full border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.5)] px-2 py-0.5">{formatNumber(totals.guias, 0)} guias</span>
            <span className="rounded-full border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.5)] px-2 py-0.5">{formatNumber(totals.bultos, 0)} bultos</span>
            <span className="rounded-full border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.5)] px-2 py-0.5">{formatNumber(totals.kilos, 2)} kg</span>
            <span className="rounded-full border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.5)] px-2 py-0.5">{formatNumber(totals.m3, 3)} m3</span>
            <span className="rounded-full border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.5)] px-2 py-0.5">{formatCurrencyShort(totals.valor)}</span>
            <span className="rounded-full border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.5)] px-2 py-0.5">Checks {status.label}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            ref={primaryAction?.ref}
            variant="strong"
            size="sm"
            onClick={primaryAction?.onClick}
            disabled={primaryAction?.disabled}
            title={primaryAction?.title || primaryAction?.label}
          >
            <span className="inline-flex items-center gap-2">
              {primaryAction?.label || 'Emitir foja'}
              <ShortcutChip>F5</ShortcutChip>
            </span>
          </Button>
          <details className="relative">
            <summary className="flex h-8 cursor-pointer list-none items-center gap-2 rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.82)] px-3 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.92)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              Mas acciones
              <ChevronDown size={14} />
            </summary>
              <div className="absolute right-0 top-10 min-w-[220px] rounded-2xl border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.98)] p-2 shadow-[0_18px_40px_rgba(var(--shadow),0.22)]">
                <div className="flex flex-col gap-1">
                  {onEditSelection ? (
                    <Button variant="outline" size="sm" onClick={onEditSelection.onClick} disabled={onEditSelection.disabled}>
                      {onEditSelection.label}
                  </Button>
                ) : null}
                <Button variant="outline" size="sm" onClick={onRefreshAction} disabled={false}>
                  <RefreshCw size={14} />
                  Actualizar
                </Button>
                <Button variant="outline" size="sm" onClick={onPrintAction} disabled={!onPrintAction}>
                  <Printer size={14} />
                  Reimprimir
                </Button>
                <Button variant="outline" size="sm" onClick={onEmitValorizada} disabled={isSubmitting || isFojaEmitted}>
                  Emitir valorizada
                </Button>
              </div>
            </div>
          </details>
        </div>
      </div>
    </section>
  );
}

function FojaOperationalBar({
  lineas,
  selectedLinea,
  setSelectedLinea,
  selectedLineaCatalog,
  guiasLength,
  isFojaEmitted,
  isLoading,
  loadingLineasCatalog,
  loadingChoferes,
  choferOptions,
  vehiculoOptions,
  form,
  setForm,
  compactControlClass,
  lineSelectRef,
  choferSelectRef,
  vehiculoSelectRef,
  modoRecepcionRef,
  choferContextRef,
  vehiculoContextRef,
  onQuickChooseChofer,
  onQuickChooseVehiculo,
  onFocusChofer,
  onFocusVehiculo,
  onFocusMode,
  onFocusScan,
}) {
  const safeLineas = Array.isArray(lineas) ? lineas : [];
  const safeChoferOptions = Array.isArray(choferOptions) ? choferOptions : [];
  const safeVehiculoOptions = Array.isArray(vehiculoOptions) ? vehiculoOptions : [];
  const safeForm = form || {};

  return (
    <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.94)] p-2.5 shadow-[0_12px_28px_rgba(var(--shadow),0.08)]">
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-12">
        <div className="xl:col-span-3">
          <OperationalCombobox
            ref={lineSelectRef}
            id="foja-linea-operativa"
            label="Línea"
            value={selectedLinea}
            options={safeLineas.map((item) => ({
              value: String(item.id),
              label: `${item.codigo} - ${item.nombre}`,
              helperText: `Línea ${item.codigo || item.id}`,
              searchText: [item.codigo, item.nombre, item.id].filter(Boolean).join(' '),
            }))}
            placeholder={loadingLineasCatalog ? 'Cargando líneas...' : 'Seleccionar línea'}
            loading={loadingLineasCatalog}
            loadingLabel="Cargando líneas..."
            disabled={isFojaEmitted}
            inputClassName={compactControlClass}
            onChange={(value) => !isFojaEmitted && setSelectedLinea(String(value))}
            onCommitNext={onFocusChofer}
          />
        </div>

        <div className="xl:col-span-3">
          <label className={`${compactFieldLabelClassName} mb-0.5`}>Destino / tramo</label>
          <div className="flex h-9 items-center rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgba(var(--subpanel),0.7)] px-3 text-[13px] text-[rgba(var(--fg),0.82)]">
            <span className="truncate">
              {selectedLineaCatalog ? selectedLineaCatalog.nombre : guiasLength ? 'Tramo asociado a la línea' : 'Definí una línea para ver el tramo'}
            </span>
          </div>
        </div>

        <div ref={choferContextRef} className="xl:col-span-2">
          <div className={compactFieldHeaderClassName}>
            <label className={compactFieldLabelClassName}>Chofer</label>
            <Button type="button" variant="ghost" size="sm" className={compactFieldActionClassName} onClick={onQuickChooseChofer} disabled={isFojaEmitted}>
              + Nuevo chofer
            </Button>
          </div>
          <OperationalCombobox
            ref={choferSelectRef}
            value={safeForm.chofer_id || ''}
            options={safeChoferOptions.map((item) => ({
              value: item.id,
              label: item.label,
              helperText: 'Buscar por nombre o apellido',
              searchText: item.label,
            }))}
            placeholder={loadingChoferes ? 'Cargando choferes...' : 'Sin asignar'}
            loading={loadingChoferes}
            loadingLabel="Cargando choferes..."
            disabled={isFojaEmitted}
            inputClassName={compactControlClass}
            onChange={(value) => setForm((prev) => ({ ...prev, chofer_id: String(value) }))}
            onCommitNext={onFocusVehiculo}
          />
        </div>

        <div ref={vehiculoContextRef} className="xl:col-span-2">
          <div className={compactFieldHeaderClassName}>
            <label className={compactFieldLabelClassName}>Unidad</label>
            <Button type="button" variant="ghost" size="sm" className={compactFieldActionClassName} onClick={onQuickChooseVehiculo} disabled={isFojaEmitted}>
              + Nueva unidad
            </Button>
          </div>
          <OperationalCombobox
            ref={vehiculoSelectRef}
            value={safeForm.vehiculo_id || ''}
            options={safeVehiculoOptions.map((item) => ({
              value: item.id,
              label: item.label,
              helperText: 'Buscar por patente o modelo',
              searchText: item.label,
            }))}
            placeholder={isLoading ? 'Cargando unidades...' : 'Sin asignar'}
            loading={isLoading}
            loadingLabel="Cargando unidades..."
            disabled={isFojaEmitted}
            inputClassName={compactControlClass}
            onChange={(value) => setForm((prev) => ({ ...prev, vehiculo_id: String(value) }))}
            onCommitNext={onFocusMode}
          />
        </div>

        <div className="xl:col-span-2">
          <label className={`${compactFieldLabelClassName} mb-0.5`}>Modo de recepción</label>
          <Select
            ref={modoRecepcionRef}
            className={compactControlClass}
            value={safeForm.modo_recepcion || 'manual_por_guia'}
            onChange={(event) => setForm((prev) => ({ ...prev, modo_recepcion: event.target.value }))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onFocusScan?.();
              }
            }}
            disabled={isFojaEmitted}
          >
            {RECEPCION_MODOS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </Select>
        </div>
      </div>
    </section>
  );
}

function ContextualOperationalChecklist({ items, summaryLabel, isTouchLike, onItemFocus }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const safeItems = Array.isArray(items) ? items : [];

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const pendingCount = safeItems.filter((item) => item.state === 'pending').length;
  const warningCount = safeItems.filter((item) => item.state === 'warning').length;
  const dangerCount = safeItems.filter((item) => item.state === 'danger').length;
  const missingCount = safeItems.filter((item) => item.state !== 'ok').length;

  const tone = dangerCount ? 'danger' : warningCount ? 'warning' : pendingCount ? 'warning' : 'success';
  const badgeLabel = dangerCount
    ? 'Bloqueado'
    : missingCount
      ? `Faltan ${missingCount}`
      : 'Checklist OK';
  const statusLabel = dangerCount ? 'Bloqueado' : warningCount ? 'Atención' : pendingCount ? 'Pendiente' : 'OK';

  return (
    <div
      ref={rootRef}
      className="relative inline-flex w-full max-w-[380px] xl:w-auto"
      onMouseEnter={() => !isTouchLike && setOpen(true)}
      onMouseLeave={() => !isTouchLike && setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={[
          'inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-2 text-[12px] font-semibold shadow-[0_8px_18px_rgba(var(--shadow),0.12)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
          tone === 'danger'
            ? 'border-[rgba(var(--danger),0.32)] bg-[rgba(var(--danger),0.11)] text-[rgb(var(--danger))]'
            : tone === 'warning'
              ? 'border-[rgba(var(--warning),0.3)] bg-[rgba(var(--warning),0.12)] text-[rgb(var(--warning))]'
              : 'border-[rgba(var(--brand),0.28)] bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand-700))]',
        ].join(' ')}
        aria-expanded={open}
      >
        <Route size={14} />
        <span className="truncate">Checklist operativo</span>
        <span className="rounded-full bg-[rgba(var(--panel),0.82)] px-2 py-0.5 text-[10px] font-semibold tracking-[0.08em] text-[rgba(var(--fg),0.7)]">
          {badgeLabel}
        </span>
        <span className="rounded-full bg-[rgba(var(--panel),0.82)] px-2 py-0.5 text-[10px] font-semibold tracking-[0.08em] text-[rgba(var(--fg),0.7)]">
          {statusLabel}
        </span>
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+8px)] z-40 w-[min(380px,calc(100vw-1.5rem))] rounded-2xl border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.98)] p-3 shadow-[0_14px_28px_rgba(var(--shadow),0.14)] backdrop-blur">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.55)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.6)]">
                <Route size={11} />
                <span>Checklist operativo</span>
                <span className="text-[rgba(var(--fg),0.48)]">·</span>
                <span>{badgeLabel}</span>
              </div>
              <div className="mt-1 text-sm font-semibold text-[rgb(var(--fg))]">{summaryLabel}</div>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-8 px-2 text-[12px]">
              Cerrar
            </Button>
          </div>
          <div className="mt-3 grid gap-1.5">
            {safeItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  onItemFocus(item);
                  setOpen(false);
                }}
                className={[
                  'flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-[13px] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                  item.state === 'ok'
                    ? 'border-[rgba(var(--brand),0.18)] bg-[rgba(var(--brand),0.08)]'
                    : item.state === 'warning'
                      ? 'border-[rgba(var(--warning),0.22)] bg-[rgba(var(--warning),0.08)]'
                      : item.state === 'danger'
                        ? 'border-[rgba(var(--danger),0.22)] bg-[rgba(var(--danger),0.08)]'
                        : 'border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.56)]',
                ].join(' ')}
              >
                <span className="min-w-0">
                  <span className="block font-medium">{item.label}</span>
                  {item.detail ? <span className="block truncate text-[11px] text-[rgba(var(--fg),0.6)]">{item.detail}</span> : null}
                </span>
                <span className="inline-flex shrink-0 items-center gap-1 font-medium">
                  {item.state === 'ok' ? (
                    <>
                      <CheckCircle2 size={14} className="text-[rgb(var(--brand))]" />
                      <span>OK</span>
                    </>
                  ) : item.state === 'warning' ? (
                    <>
                      <AlertTriangle size={14} className="text-[rgb(var(--warning))]" />
                      <span>Atención</span>
                    </>
                  ) : item.state === 'danger' ? (
                    <>
                      <AlertTriangle size={14} className="text-[rgb(var(--danger))]" />
                      <span>Bloqueado</span>
                    </>
                  ) : (
                    <span className="text-[rgba(var(--fg),0.56)]">Pendiente</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EmitFojaModal({
  isOpen,
  onClose,
  onConfirm,
  form,
  setForm,
  selectedLineaCatalog,
  selectedIdsLength,
  canAutoDispatch,
  isSubmitting,
  isFojaEmitted,
  initialFocusRef,
  observacionesRef,
  precinto1Ref,
  precinto2Ref,
  precinto3Ref,
}) {
  const safeForm = form || {};
  const safeSelectedLinea = selectedLineaCatalog || null;
  const selectedGuiaLabel = `${formatNumber(selectedIdsLength || 0, 0)} guia${selectedIdsLength === 1 ? '' : 's'} seleccionada${selectedIdsLength === 1 ? '' : 's'}`;

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="modal-emitir-foja"
      initialFocusRef={initialFocusRef}
      className="modal modal-content w-full max-w-[760px] overflow-hidden rounded-[18px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.985)] p-0 shadow-[0_30px_80px_rgba(var(--shadow),0.34)] focus-visible:outline-none"
      viewportClassName="modal-viewport px-3 py-3 md:px-5"
    >
      <div className="flex h-full min-h-0 flex-col bg-[rgba(var(--panel),0.98)]">
        <div className="border-b border-[rgba(var(--border),0.3)] px-4 py-3 md:px-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgba(var(--fg),0.5)]">Armado operativo</div>
              <h3 id="modal-emitir-foja" className="mt-1 text-base font-semibold text-[rgb(var(--fg))]">Completar emisión de foja</h3>
              <p className="mt-1 text-sm text-[rgba(var(--fg),0.64)]">
                {safeSelectedLinea
                  ? `Línea ${safeSelectedLinea.codigo || safeSelectedLinea.id} · ${safeSelectedLinea.nombre}`
                  : 'Seleccioná una línea para continuar.'}
              </p>
            </div>
            <div className="rounded-full border border-[rgba(var(--border),0.42)] bg-[rgba(var(--subpanel),0.7)] px-3 py-1 text-xs font-medium text-[rgba(var(--fg),0.72)]">
              {selectedGuiaLabel}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-5">
          <div className="grid gap-3">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">Observaciones</label>
              <Textarea
                ref={observacionesRef}
                value={safeForm.observaciones || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, observaciones: event.target.value }))}
                placeholder="Observaciones operativas."
                className="min-h-[96px] rounded-xl px-3 py-2 text-[13px] leading-5"
                disabled={isFojaEmitted}
              />
            </div>

            <div className="grid gap-2 md:grid-cols-3">
              <Input
                ref={precinto1Ref}
                className={compactControlClassName}
                placeholder="Precinto 1"
                value={safeForm.precinto_1 || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, precinto_1: event.target.value }))}
                disabled={isFojaEmitted}
              />
              <Input
                ref={precinto2Ref}
                className={compactControlClassName}
                placeholder="Precinto 2"
                value={safeForm.precinto_2 || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, precinto_2: event.target.value }))}
                disabled={isFojaEmitted}
              />
              <Input
                ref={precinto3Ref}
                className={compactControlClassName}
                placeholder="Precinto 3"
                value={safeForm.precinto_3 || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, precinto_3: event.target.value }))}
                disabled={isFojaEmitted}
              />
            </div>

            <div className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.62)] p-3">
              <label className="flex cursor-pointer items-start justify-between gap-4 md:items-center">
                <div className="min-w-0 space-y-1">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">Salida al emitir</div>
                  <p className="text-sm text-[rgba(var(--fg),0.72)]">
                    Si activás esta opción, la foja quedará marcada como salida al momento de emitir.
                  </p>
                  <div className="text-[11px] text-[rgba(var(--fg),0.56)]">
                    {canAutoDispatch ? 'Disponible para tu perfil.' : 'Requiere permiso de confirmar despacho.'}
                  </div>
                </div>
                <Switch
                  id="foja-auto-despacho"
                  checked={Boolean(safeForm.auto_confirmar_despacho)}
                  onCheckedChange={(checked) => setForm((prev) => ({ ...prev, auto_confirmar_despacho: checked }))}
                  disabled={isFojaEmitted || !canAutoDispatch}
                  aria-label="Salida al emitir"
                  className="mt-0.5"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="border-t border-[rgba(var(--border),0.32)] bg-[rgba(var(--panel),0.98)] px-4 py-3 md:px-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-[rgba(var(--fg),0.64)]">
              Revisá observaciones y precintos antes de confirmar la emisión.
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="strong"
                onClick={onConfirm}
                disabled={isSubmitting || isFojaEmitted}
              >
                {isSubmitting ? 'Emitiendo...' : 'Emitir foja'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalBase>
  );
}

function FojaGuiasPanel({
  selectedLineaCatalog,
  activeGuides,
  guias,
  isFojaEmitted,
  activeTab,
  setActiveTab,
  guideSummaryBar,
  incidents,
  timelineItems,
  scanHistory,
  openGuidesModal,
  selectedIdsSet,
  toggleSelectedGuide,
  formatNumberFn,
  STATE_VARIANTS_MAP,
  activeGuidesCount,
  checklistItems,
  checklistSummary,
  isTouchLike,
  onChecklistItemFocus,
  scanInput,
  setScanInput,
  scanFeedback,
  setScanFeedback,
  handleScanSubmit,
  scanInputRef,
}) {
  const safeGuides = Array.isArray(guias) ? guias : [];
  const safeActiveGuides = Array.isArray(activeGuides) ? activeGuides : [];
  const safeIncidents = Array.isArray(incidents) ? incidents : [];
  const safeTimelineItems = Array.isArray(timelineItems) ? timelineItems : [];
  const safeScanHistory = Array.isArray(scanHistory) ? scanHistory : [];
  const safeSelectedIdsSet = selectedIdsSet instanceof Set ? selectedIdsSet : new Set();
  const safeScanFeedback = scanFeedback || null;
  const hasScanAccess = Boolean(selectedLineaCatalog) && !isFojaEmitted && safeGuides.length > 0;

  return (
    <section className="relative flex min-h-0 flex-1 flex-col rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.94)] p-3">
      <div className="flex min-h-0 flex-col gap-3">
        <div className="flex flex-col gap-3">
          <div className="relative flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between xl:pr-[392px]">
            <div>
              <h2 className="text-sm font-semibold">Panel operativo</h2>
              <p className="mt-1 text-[12px] text-[rgba(var(--fg),0.64)]">
                {selectedLineaCatalog
                  ? `${activeGuidesCount} de ${safeGuides.length || activeGuidesCount} guías activas en la línea ${selectedLineaCatalog.codigo || selectedLineaCatalog.id}.`
                  : 'Seleccioná una línea para habilitar la operación.'}
              </p>
            </div>
            <div className="xl:hidden">
              <ContextualOperationalChecklist
                items={checklistItems}
                summaryLabel={checklistSummary}
                isTouchLike={isTouchLike}
                onItemFocus={onChecklistItemFocus}
              />
            </div>
            <div className="absolute right-0 top-0 z-20 hidden xl:block">
              <ContextualOperationalChecklist
                items={checklistItems}
                summaryLabel={checklistSummary}
                isTouchLike={isTouchLike}
                onItemFocus={onChecklistItemFocus}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-1.5">
            {[
              { id: 'guias', label: 'Guías' },
              { id: 'incidencias', label: 'Incidencias' },
              { id: 'timeline', label: 'Timeline' },
              { id: 'historial', label: 'Historial' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={[
                  'rounded-xl border px-3 py-1.5 text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                  activeTab === tab.id
                    ? 'border-[rgba(var(--brand),0.8)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]'
                    : 'border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.72)] text-[rgba(var(--fg),0.7)] hover:bg-[rgba(var(--subpanel),0.84)]',
                ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 whitespace-nowrap"
                onClick={openGuidesModal}
                disabled={!selectedLineaCatalog || isFojaEmitted}
              >
                <span className="inline-flex items-center gap-2">
                  {selectedLineaCatalog ? 'Agregar guías' : 'Elegí una línea'}
                  <ShortcutChip>F2</ShortcutChip>
                </span>
              </Button>
              {hasScanAccess ? (
                <form onSubmit={handleScanSubmit} className="flex w-full max-w-[440px] items-center gap-2">
                  <Input
                    ref={scanInputRef}
                    className="h-9 rounded-xl px-3 py-1.5 text-[13px] leading-5"
                    value={scanInput}
                    onChange={(event) => {
                      setScanInput(event.target.value);
                      if (scanFeedback) setScanFeedback(null);
                    }}
                    placeholder="Escanear o ingresar guía"
                    disabled={!selectedLineaCatalog || !safeGuides.length || isFojaEmitted}
                  />
                  <Button type="submit" variant="outline" size="sm" className="h-9 whitespace-nowrap" disabled={!selectedLineaCatalog || !safeGuides.length || isFojaEmitted}>
                    Escanear
                  </Button>
                </form>
              ) : (
                <div className="inline-flex h-9 items-center rounded-full border border-[rgba(var(--border),0.34)] bg-[rgba(var(--subpanel),0.64)] px-3 text-[12px] text-[rgba(var(--fg),0.62)]">
                  Seleccioná una línea para habilitar escaneo
                </div>
              )}
            </div>
          </div>
          {safeScanFeedback ? (
            <div className={`rounded-xl border px-3 py-2 text-sm ${SCAN_FEEDBACK_STYLES[safeScanFeedback.tone] || SCAN_FEEDBACK_STYLES.warning}`}>
              {safeScanFeedback.message}
            </div>
          ) : null}
        </div>

        {activeTab === 'guias' ? (
          <>
            <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.72)] px-3 py-2 text-[12px] text-[rgba(var(--fg),0.7)]">
              {guideSummaryBar}
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[rgba(var(--border),0.32)]">
              <div className="min-h-0 flex-1 overflow-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead className="sticky top-0 z-10 bg-[rgba(var(--subpanel),0.96)] text-left text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">
                    <tr>
                      <th className="px-3 py-3">Guia</th>
                      <th className="px-3 py-3">Cliente</th>
                      <th className="px-3 py-3">Destino</th>
                      <th className="px-3 py-3">Remito</th>
                      <th className="px-3 py-3 text-right">BLTS.</th>
                      <th className="px-3 py-3 text-right">Kg</th>
                      <th className="px-3 py-3 text-right">VD</th>
                      {!isFojaEmitted ? <th className="px-3 py-3 text-right">Acciones</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {!safeActiveGuides.length ? (
                      <tr>
                        <td colSpan={isFojaEmitted ? 7 : 8} className="px-4 py-10">
  <div className="mx-auto grid min-h-[240px] max-w-[520px] place-items-center rounded-2xl border border-dashed border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.5)] px-6 py-8 text-center">
    <div>
      <div className="text-base font-semibold">
        {selectedLineaCatalog ? 'Todavía no agregaste guías' : 'Elegí una línea'}
      </div>
      <p className="mt-2 text-sm text-[rgba(var(--fg),0.64)]">
        {selectedLineaCatalog
          ? 'Seleccioná guías disponibles o escaneá una guía para cargar la foja.'
          : 'Elegí una línea para habilitar la selección de guías.'}
      </p>
      {!isFojaEmitted ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openGuidesModal}
          disabled={!selectedLineaCatalog}
          className="mt-4"
        >
          <span className="inline-flex items-center gap-2">
            {selectedLineaCatalog ? 'Agregar guías' : 'Elegí una línea'}
            <ShortcutChip>F2</ShortcutChip>
          </span>
        </Button>
      ) : null}
    </div>
  </div>
</td>
                      </tr>
                    ) : (
                      safeActiveGuides.map((guia) => (
                        <tr
                          key={guia.id}
                          className={[
                            'border-t border-[rgba(var(--border),0.22)] transition-colors',
                            safeSelectedIdsSet.has(guia.id) ? 'bg-[rgba(var(--brand),0.08)]' : 'hover:bg-[rgba(var(--brand),0.05)]',
                          ].join(' ')}
                        >
                          <td className="px-2 py-2 align-top">
                            <div className="truncate font-semibold text-[rgb(var(--fg))]" title={String(guia.numero_guia || guia.id)}>
                              {guia.numero_guia || guia.id}
                            </div>
                            <div className="mt-1">
                              <Badge variant={STATE_VARIANTS_MAP[String(guia.estado || '').toLowerCase()] || 'outline'} className="px-2 py-0.5 text-[9px] tracking-[0.14em]">
                                {String(guia.estado || 'sin estado').replaceAll('_', ' ')}
                              </Badge>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">{getDestinatarioDisplay(guia).primary || getRemitenteDisplay(guia).primary || DISPLAY_FALLBACK}</td>
                          <td className="px-3 py-2.5">
                            <div className="truncate" title={getDestinoDisplay(guia).title || DISPLAY_FALLBACK}>{getDestinoDisplay(guia).primary || DISPLAY_FALLBACK}</div>
                            {getDestinoDisplay(guia).secondary ? (
                              <div className="truncate text-[11px] text-[rgba(var(--fg),0.58)]" title={getDestinoDisplay(guia).secondary}>
                                {getDestinoDisplay(guia).secondary}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5">{getRemitoDisplay(guia).primary || DISPLAY_FALLBACK}</td>
                          <td className="px-3 py-2.5 text-right">{formatNumberFn(guia.bultos, 0)}</td>
                          <td className="px-3 py-2.5 text-right">{formatNumberFn(guia.kg_reales, 2)}</td>
                          <td className="px-3 py-2.5 text-right">{getValorDeclaradoDisplay(guia).primary || DISPLAY_FALLBACK}</td>
                          {!isFojaEmitted ? (
                            <td className="px-3 py-2.5 text-right">
                              <Button variant="ghost" size="sm" title="Quitar de la foja" aria-label={`Quitar guía ${guia.numero_guia || guia.id} de la foja`} onClick={() => toggleSelectedGuide(guia.id)}>Quitar</Button>
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}

        {activeTab === 'incidencias' ? (
          <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.68)] p-3">
            {!safeIncidents.length ? (
              <div className="text-sm text-[rgba(var(--fg),0.62)]">Sin incidencias registradas para las guias visibles.</div>
            ) : (
              <div className="space-y-2">
                {safeIncidents.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.7)] px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold">{item.numero_guia || item.id}</div>
                      <Badge variant="warning" className="px-2 py-0.5 text-[9px]">{item.incidencia_recepcion || 'Con observacion'}</Badge>
                    </div>
                    <div className="mt-1 text-sm text-[rgba(var(--fg),0.7)]">{item.observaciones_recepcion || 'Sin detalle adicional.'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {activeTab === 'timeline' ? (
          <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.68)] p-3">
            {!safeTimelineItems.length ? (
              <div className="text-sm text-[rgba(var(--fg),0.62)]">La timeline se habilita cuando la foja ya tiene eventos persistidos.</div>
            ) : (
              <div className="space-y-2">
                {safeTimelineItems.map((item, index) => (
                  <div key={item.id || `${item.fecha || 'item'}-${index}`} className="flex gap-3 rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.72)] px-3 py-2">
                    <TimerReset size={16} className="mt-0.5 shrink-0 text-[rgba(var(--fg),0.55)]" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{item.evento || item.tipo || 'Evento operativo'}</div>
                      <div className="text-[12px] text-[rgba(var(--fg),0.62)]">{item.fecha || item.created_at || item.fecha_evento || 'Sin fecha'}</div>
                      {item.descripcion || item.detalle ? <div className="mt-1 text-sm text-[rgba(var(--fg),0.72)]">{item.descripcion || item.detalle}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {activeTab === 'historial' ? (
          <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.68)] p-3">
            {!safeScanHistory.length ? (
              <div className="text-sm text-[rgba(var(--fg),0.62)]">Todavia no hay historial de scans o movimientos registrados.</div>
            ) : (
              <div className="space-y-2">
                {safeScanHistory.map((item, index) => (
                  <div key={item.id || `${item.fecha_scan || 'scan'}-${index}`} className="flex gap-3 rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.72)] px-3 py-2">
                    <History size={16} className="mt-0.5 shrink-0 text-[rgba(var(--fg),0.55)]" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{item.tipo_scan || 'Movimiento'}</div>
                      <div className="text-[12px] text-[rgba(var(--fg),0.62)]">{item.fecha_scan || item.created_at || 'Sin fecha'}</div>
                      <div className="mt-1 text-sm text-[rgba(var(--fg),0.72)]">
                        {[item.numero_guia, item.resultado, item.observaciones].filter(Boolean).join(' / ') || 'Sin detalle adicional.'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function EmisionFojaFallback({ error, onRetry }) {
  const isDev = import.meta.env.DEV;

  return (
    <div className="mx-auto flex max-w-[980px] flex-col gap-4 px-4 py-6 text-[rgb(var(--fg))]">
      <section className="rounded-[24px] border border-red-400/35 bg-[rgba(var(--panel),0.96)] p-5 shadow-[0_20px_50px_rgba(var(--shadow),0.14)]">
        <div className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-4">
          <h1 className="text-lg font-semibold">No pudimos abrir Emision de foja</h1>
          <p className="mt-2 text-sm text-[rgba(var(--fg),0.74)]">
            La navegacion principal sigue disponible. Reintenta sin salir del circuito de Cargas.
          </p>
          {isDev && error?.message ? (
            <pre className="mt-3 overflow-x-auto rounded-xl bg-[rgba(var(--subpanel),0.8)] p-3 text-xs text-[rgba(var(--fg),0.84)]">
              {error.message}
            </pre>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={onRetry}>Reintentar</Button>
            <Link
              to="/cargas/fojas-de-ruta"
              className="inline-flex h-10 items-center justify-center rounded-xl border border-[rgba(var(--border),0.6)] px-4 text-sm font-semibold"
            >
              Volver a fojas
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

class EmisionFojaErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, key: 0 };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error('EmisionFoja render error', error);
  }

  handleRetry = () => {
    this.setState((prev) => ({ error: null, key: prev.key + 1 }));
  };

  render() {
    if (this.state.error) {
      return <EmisionFojaFallback error={this.state.error} onRetry={this.handleRetry} />;
    }

    return <div key={this.state.key}>{this.props.children}</div>;
  }
}

function EmisionFojaContent() {
  const auth = useAuth();
  const client = useClient();
  const sucursal = getSucursalInfo(auth);
  const empresaId = getEmpresaId(auth);
  const canAutoDispatch = hasPermission(auth?.user ?? auth, 'FOJAS_CONFIRMAR_DESPACHO');
  const canOverrideLimits = hasPermission(auth?.user ?? auth, 'FOJAS_OVERRIDE_LIMITES');

  const [lineas, setLineas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [quickChoferes, setQuickChoferes] = useState([]);
  const [selectedLinea, setSelectedLinea] = useState('');
  const [guias, setGuias] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalSelectedIds, setModalSelectedIds] = useState([]);
  const [fojaDetail, setFojaDetail] = useState(null);
  const [showChoferModal, setShowChoferModal] = useState(false);
  const [showVehiculoModal, setShowVehiculoModal] = useState(false);
  const [showGuidesModal, setShowGuidesModal] = useState(false);
  const [guideSearch, setGuideSearch] = useState('');
  const [guideFilters, setGuideFilters] = useState({ localidad: '', cliente: '', estado: '' });
  const deferredGuideSearch = useDeferredValue(guideSearch);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingChoferes, setLoadingChoferes] = useState(false);
  const [loadingGuias, setLoadingGuias] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [policyFeedback, setPolicyFeedback] = useState(null);
  const [showEmitModal, setShowEmitModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [pendingEmitOptions, setPendingEmitOptions] = useState(null);
  const [activeTab, setActiveTab] = useState('guias');
  const [scanInput, setScanInput] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);
  const scanInputRef = useRef(null);
  const lineSelectRef = useRef(null);
  const choferSelectRef = useRef(null);
  const vehiculoSelectRef = useRef(null);
  const modoRecepcionRef = useRef(null);
  const choferContextRef = useRef(null);
  const vehiculoContextRef = useRef(null);
  const guidesActionButtonRef = useRef(null);
  const didAutoFocusLineRef = useRef(false);
  const precinto1Ref = useRef(null);
  const precinto2Ref = useRef(null);
  const precinto3Ref = useRef(null);
  const observacionesRef = useRef(null);
  const [modalScanInput, setModalScanInput] = useState('');
  const [modalScanFeedback, setModalScanFeedback] = useState(null);
  const modalScanInputRef = useRef(null);
  const [form, setForm] = useState({
    modo_recepcion: 'manual_por_guia',
    chofer_id: '',
    vehiculo_id: '',
    auto_confirmar_despacho: false,
    precinto_1: '',
    precinto_2: '',
    precinto_3: '',
    observaciones: '',
  });

  const [{ data: lineasCatalogData, fetching: loadingLineasCatalog }] = useQuery({
    query: LINEAS_QUERY,
    variables: { sucursalId: Number(sucursal.id) },
    pause: !sucursal.id,
  });

  const lineasCatalog = useMemo(
    () =>
      (Array.isArray(lineasCatalogData?.lineas) ? lineasCatalogData.lineas : []).map((item) => ({
        id: String(item.id),
        codigo: item.codigo || String(item.id),
        nombre: item.nombre || item.descripcion || `Linea ${item.id}`,
      })),
    [lineasCatalogData?.lineas],
  );

  const lineaSummaryMap = useMemo(() => {
    const map = new Map();
    lineas.forEach((item) => {
      const key = getLineaSummaryKey(item);
      if (!key) return;
      const current = map.get(key) || { total_guias: 0, total_bultos: 0, total_kilos: 0, total_valor_declarado: 0 };
      current.total_guias += Number(item.total_guias || 0);
      current.total_bultos += Number(item.total_bultos || 0);
      current.total_kilos += Number(item.total_kilos || 0);
      current.total_valor_declarado += Number(item.total_valor_declarado || 0);
      map.set(key, current);
    });
    return map;
  }, [lineas]);

  const lineaCards = useMemo(() => {
    const cards = lineasCatalog.map((item) => ({
      ...item,
      ...lineaSummaryMap.get(String(item.id)),
    }));

    lineas.forEach((item) => {
      const key = getLineaSummaryKey(item);
      if (!key || cards.some((card) => String(card.id) === key)) return;
      cards.push({
        id: key,
        codigo: key,
        nombre: `Linea ${key}`,
        ...(lineaSummaryMap.get(key) || {}),
      });
    });

    return cards.sort((a, b) => String(a.codigo).localeCompare(String(b.codigo), 'es'));
  }, [lineaSummaryMap, lineas, lineasCatalog]);

  const selectedLineaCatalog = useMemo(
    () => lineaCards.find((item) => String(item.id) === String(selectedLinea)) || null,
    [lineaCards, selectedLinea],
  );

  const choferOptions = useMemo(() => {
    const map = new Map();
    [...choferes, ...quickChoferes].forEach((item) => {
      map.set(String(item.id), {
        id: String(item.id),
        label: [item.apellido, item.nombre].filter(Boolean).join(', ') || item.nombreCompleto || `Chofer ${item.id}`,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [choferes, quickChoferes]);

  const vehiculoOptions = useMemo(
    () =>
      vehiculos.map((item) => ({
        id: String(item.id),
        label: [item.patente, [item.marca, item.modelo].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || 'Unidad',
      })),
    [vehiculos],
  );

  const selectedVehiculo = useMemo(
    () => vehiculos.find((item) => String(item.id) === String(form.vehiculo_id)) || null,
    [form.vehiculo_id, vehiculos],
  );

  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const modalSelectedSet = useMemo(() => new Set(modalSelectedIds), [modalSelectedIds]);
  const safeGuides = Array.isArray(guias) ? guias : [];
  const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];

  const selectedGuides = useMemo(
    () => guias.filter((item) => selectedIdsSet.has(item.id)),
    [guias, selectedIdsSet],
  );

  const modalTotals = useMemo(
    () =>
      guias.reduce(
        (acc, guia) => {
          if (!modalSelectedSet.has(guia.id)) return acc;
          acc.guias += 1;
          acc.bultos += Number(guia.bultos || 0);
          acc.kilos += Number(guia.kg_reales || 0);
          acc.m3 += Number(guia.m3 || 0);
          acc.valor += Number(guia.valor_declarado || guia.total || 0);
          return acc;
        },
        { guias: 0, bultos: 0, kilos: 0, m3: 0, valor: 0 },
      ),
    [guias, modalSelectedSet],
  );

  const localidadOptions = useMemo(
    () =>
      Array.from(
        new Set(
          guias
            .map((item) => getDestinoDisplay(item).primary)
            .filter((value) => hasMeaningfulText(value) && value !== DISPLAY_FALLBACK),
        ),
      ).sort((a, b) => a.localeCompare(b, 'es')),
    [guias],
  );

  const clientOptions = useMemo(
    () =>
      Array.from(
        new Set(
          guias.flatMap((item) => [getClienteDisplay(item).primary, getRemitenteDisplay(item).primary, getDestinatarioDisplay(item).primary].filter((value) => hasMeaningfulText(value) && value !== DISPLAY_FALLBACK)),
        ),
      ).sort((a, b) => a.localeCompare(b, 'es')),
    [guias],
  );

  const estadoOptions = useMemo(
    () => Array.from(new Set(guias.map((item) => item.estado).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'es')),
    [guias],
  );

  const searchableGuides = useMemo(
    () =>
      guias.map((guide) => {
        const clienteDisplay = getClienteDisplay(guide);
        const remitenteDisplay = getRemitenteDisplay(guide);
        const destinatarioDisplay = getDestinatarioDisplay(guide);
        const destinoDisplay = getDestinoDisplay(guide);
        const remitoDisplay = getRemitoDisplay(guide);
        const valorDeclaradoDisplay = getValorDeclaradoDisplay(guide);

        return {
          ...guide,
          clienteDisplay,
          remitenteDisplay,
          destinatarioDisplay,
          destinoDisplay,
          remitoDisplay,
          valorDeclaradoDisplay,
          searchIndex: [
            guide.numero_guia,
            guide.id,
            clienteDisplay.primary,
            remitenteDisplay.primary,
            destinatarioDisplay.primary,
            remitoDisplay.primary,
            destinoDisplay.primary,
            destinoDisplay.secondary,
            guide.estado,
          ]
            .map(normalizeGuideLookup)
            .join(' '),
        };
      }),
    [guias],
  );

  const filteredGuides = useMemo(() => {
    const normalizedSearch = normalizeGuideLookup(deferredGuideSearch);
    return searchableGuides.filter((guide) => {
      if (normalizedSearch && !guide.searchIndex.includes(normalizedSearch)) return false;
      if (guideFilters.localidad && String(guide.destinoDisplay?.primary || '') !== guideFilters.localidad) return false;
      if (guideFilters.estado && String(guide.estado || '') !== guideFilters.estado) return false;
      if (
        guideFilters.cliente
        && ![guide.clienteDisplay?.primary, guide.remitenteDisplay?.primary, guide.destinatarioDisplay?.primary].filter(Boolean).includes(guideFilters.cliente)
      ) return false;
      return true;
    });
  }, [deferredGuideSearch, guideFilters, searchableGuides]);

  const filteredGuideIds = useMemo(() => filteredGuides.map((item) => item.id), [filteredGuides]);

  const modoRecepcionLabel = useMemo(
    () => RECEPCION_MODOS.find((item) => item.value === form.modo_recepcion)?.label || 'Manual por guia',
    [form.modo_recepcion],
  );

  const estadoFoja = fojaDetail?.foja?.estado || 'sin_crear';
  const isFojaEmitted = Boolean(fojaDetail?.foja?.id);
  const activeGuides = useMemo(
    () => (isFojaEmitted ? (Array.isArray(fojaDetail?.guias) ? fojaDetail.guias : []) : selectedGuides),
    [fojaDetail?.guias, isFojaEmitted, selectedGuides],
  );
  const safeActiveGuides = Array.isArray(activeGuides) ? activeGuides : [];
  const activeTotals = useMemo(
    () =>
      activeGuides.reduce(
        (acc, guia) => {
          acc.guias += 1;
          acc.bultos += Number(guia.bultos || 0);
          acc.kilos += Number(guia.kg_reales || 0);
          acc.m3 += Number(guia.m3 || 0);
          acc.valor += Number(guia.valor_declarado || guia.total || 0);
          return acc;
        },
        { guias: 0, bultos: 0, kilos: 0, m3: 0, valor: 0 },
      ),
    [activeGuides],
  );

  const openGuidesModal = useCallback(() => {
    if (!selectedLinea || isFojaEmitted) return;
    setModalSelectedIds(selectedIds);
    setModalScanInput('');
    setModalScanFeedback(null);
    setActiveTab('guias');
    setShowGuidesModal(true);
  }, [isFojaEmitted, selectedIds, selectedLinea]);

  const closeGuidesModal = useCallback(() => {
    setShowGuidesModal(false);
    setModalScanFeedback(null);
    window.requestAnimationFrame(() => {
      if (scanInputRef.current && !scanInputRef.current.disabled) {
        scanInputRef.current.focus?.();
        return;
      }

      guidesActionButtonRef.current?.focus?.();
    });
  }, []);

  const openEmitModal = useCallback((options = {}) => {
    if (isFojaEmitted || isSubmitting) return;
    if (!selectedLinea) {
      setErrorMessage('Debes seleccionar una linea antes de emitir la foja.');
      return;
    }
    if (!selectedIds.length) {
      setErrorMessage('Seleccioná al menos una guía antes de emitir.');
      return;
    }

    setErrorMessage('');
    setPendingEmitOptions({ printMode: options.printMode || null });
    setShowEmitModal(true);
  }, [isFojaEmitted, isSubmitting, selectedIds.length, selectedLinea]);

  const checklistItems = useMemo(
    () => [
      {
        key: 'linea',
        label: 'Línea',
        state: selectedLinea ? 'ok' : 'pending',
        detail: selectedLineaCatalog ? selectedLineaCatalog.nombre : 'Seleccionar línea operativa',
        focus: () => lineSelectRef.current?.focus(),
      },
      {
        key: 'chofer',
        label: 'Chofer',
        state: form.chofer_id ? 'ok' : 'pending',
        detail: form.chofer_id ? 'Asignado' : 'Sin asignar',
        focus: () => choferSelectRef.current?.focus(),
      },
      {
        key: 'unidad',
        label: 'Unidad',
        state: form.vehiculo_id ? 'ok' : 'pending',
        detail: form.vehiculo_id ? 'Asignada' : 'Sin asignar',
        focus: () => vehiculoSelectRef.current?.focus(),
      },
      {
        key: 'guias',
        label: 'Guías listas',
        state: activeGuides.length > 0 ? 'ok' : 'pending',
        detail: activeGuides.length ? `${formatNumber(activeGuides.length, 0)} incluidas` : 'Sin guías',
        focus: () => openGuidesModal(),
      },
      {
        key: 'limites',
        label: 'Límites',
        state: policyFeedback?.allowed === false ? 'danger' : Array.isArray(policyFeedback?.warnings) && policyFeedback.warnings.length ? 'warning' : 'ok',
        detail: policyFeedback?.allowed === false ? 'Bloqueada por límites' : Array.isArray(policyFeedback?.warnings) && policyFeedback.warnings.length ? 'Revisar límites' : 'Límites OK',
        focus: () => openGuidesModal(),
      },
    ],
    [
      activeGuides.length,
      form.chofer_id,
      form.vehiculo_id,
      openGuidesModal,
      policyFeedback,
      selectedLinea,
      selectedLineaCatalog,
    ],
  );
  const checklistSummary = useMemo(() => {
    const missingCount = checklistItems.filter((item) => item.state !== 'ok').length;
    const dangerCount = checklistItems.some((item) => item.state === 'danger');
    const warningCount = checklistItems.some((item) => item.state === 'warning');
    if (dangerCount) return 'Bloqueado';
    if (!missingCount) return 'Listo para emitir';
    if (warningCount) return `Faltan ${missingCount}`;
    return `Faltan ${missingCount}`;
  }, [checklistItems]);
  const guideSummaryBar = useMemo(
    () => `${formatNumber(activeTotals.guias, 0)} guías · ${formatNumber(activeTotals.bultos, 0)} bultos · ${formatNumber(activeTotals.kilos, 2)} kg · ${formatNumber(activeTotals.m3, 3)} m³ · ${formatCurrencyShort(activeTotals.valor)}`,
    [activeTotals.bultos, activeTotals.guias, activeTotals.kilos, activeTotals.m3, activeTotals.valor],
  );
  const incidents = useMemo(
    () => activeGuides.filter((item) => item.incidencia_recepcion || item.observaciones_recepcion),
    [activeGuides],
  );
  const timelineItems = useMemo(() => (Array.isArray(fojaDetail?.historial) ? fojaDetail.historial : []), [fojaDetail?.historial]);
  const scanHistory = useMemo(() => (Array.isArray(fojaDetail?.scans) ? fojaDetail.scans : []), [fojaDetail?.scans]);
  const touchLike = useTouchLikeMedia();

  const refreshBaseData = async () => {
    if (!sucursal.id) return;
    setIsLoading(true);
    setErrorMessage('');
    try {
      const [lineaData, vehiculosData] = await Promise.all([
        listFojaLineas({ sucursal_origen_id: sucursal.id }),
        fetchVehiculos({ estado: 'activo', sucursal_id: sucursal.id }),
      ]);
      setLineas(Array.isArray(lineaData) ? lineaData : []);
      setVehiculos(Array.isArray(vehiculosData) ? vehiculosData : []);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'No se pudieron cargar los datos base de foja.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshBaseData();
  }, [sucursal.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sucursal.id) {
      setChoferes([]);
      return;
    }

    let ignore = false;
    setLoadingChoferes(true);

    client
      .query(
        CHOFERES_LIST,
        {
          page: 1,
          pageSize: 200,
          filter: {
            sucursalId: String(sucursal.id),
            empresaId: empresaId ? String(empresaId) : undefined,
            estados: ['ACTIVO'],
          },
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise()
      .then((result) => {
        if (ignore) return;
        if (result.error) throw result.error;
        setChoferes(Array.isArray(result.data?.choferes?.rows) ? result.data.choferes.rows : []);
      })
      .catch((error) => {
        if (ignore) return;
        setChoferes([]);
        setErrorMessage((prev) => prev || error?.message || 'No se pudieron cargar los choferes.');
      })
      .finally(() => {
        if (!ignore) setLoadingChoferes(false);
      });

    return () => {
      ignore = true;
    };
  }, [client, empresaId, sucursal.id]);

  useEffect(() => {
    if (!selectedLinea) {
      setGuias([]);
      setSelectedIds([]);
      setModalSelectedIds([]);
      setGuideSearch('');
      setGuideFilters({ localidad: '', cliente: '', estado: '' });
      setScanInput('');
      setScanFeedback(null);
      setModalScanInput('');
      setModalScanFeedback(null);
      setPolicyFeedback(null);
      setShowOverrideModal(false);
      setOverrideReason('');
      setPendingEmitOptions(null);
      setActiveTab('guias');
      return;
    }
    setGuias([]);
    setSelectedIds([]);
    setModalSelectedIds([]);
    setFojaDetail(null);
    setShowGuidesModal(false);
    setGuideSearch('');
    setGuideFilters({ localidad: '', cliente: '', estado: '' });
    setScanInput('');
    setScanFeedback(null);
    setModalScanInput('');
    setModalScanFeedback(null);
    setSuccessMessage('');
    setErrorMessage('');
    setPolicyFeedback(null);
    setShowOverrideModal(false);
    setOverrideReason('');
    setPendingEmitOptions(null);
    setActiveTab('guias');
  }, [selectedLinea]);

  useEffect(() => {
    if (!selectedLineaCatalog || isFojaEmitted) {
      setLoadingGuias(false);
      return;
    }
    let ignore = false;
    setLoadingGuias(true);
    listGuiasElegibles({
      sucursal_origen_id: sucursal.id,
      linea_id: selectedLineaCatalog.id,
      linea: selectedLineaCatalog.id,
    })
      .then((data) => {
        if (ignore) return;
        setGuias(Array.isArray(data) ? data : []);
        setSelectedIds([]);
        setModalSelectedIds([]);
        setScanFeedback(null);
      })
      .catch((error) => {
        if (!ignore) setErrorMessage(getErrorMessage(error, 'No se pudieron cargar las guias elegibles.'));
      })
      .finally(() => {
        if (!ignore) setLoadingGuias(false);
      });

    return () => {
      ignore = true;
    };
  }, [isFojaEmitted, selectedLineaCatalog, sucursal.id]);

  useEffect(() => {
    if (isFojaEmitted || selectedLinea || loadingLineasCatalog || didAutoFocusLineRef.current) return;
    if (!lineSelectRef.current) return;

    didAutoFocusLineRef.current = true;
    window.requestAnimationFrame(() => {
      lineSelectRef.current?.focus?.();
    });
  }, [isFojaEmitted, loadingLineasCatalog, selectedLinea]);

  useEffect(() => {
    if (!showGuidesModal || isFojaEmitted) return;
    window.requestAnimationFrame(() => modalScanInputRef.current?.focus());
  }, [isFojaEmitted, showGuidesModal]);

  const toggleSelectedGuide = (guideId) => {
    if (isFojaEmitted) return;
    setSelectedIds((prev) => (prev.includes(guideId) ? prev.filter((item) => item !== guideId) : [...prev, guideId]));
    setScanFeedback(null);
  };

  const toggleModalGuide = (guideId) => {
    if (isFojaEmitted) return;
    setModalSelectedIds((prev) => (prev.includes(guideId) ? prev.filter((item) => item !== guideId) : [...prev, guideId]));
  };

  const applySelectedGuides = () => {
    setSelectedIds(modalSelectedIds);
    closeGuidesModal();
    setScanFeedback(null);
    setModalScanFeedback(null);
    window.requestAnimationFrame(() => {
      if (scanInputRef.current && !scanInputRef.current.disabled) {
        scanInputRef.current.focus?.();
        return;
      }

      guidesActionButtonRef.current?.focus?.();
    });
  };

  const findGuideByLookup = (rawValue) => {
    const normalized = normalizeGuideLookup(rawValue);
    const guiaIdFromQr = extractGuiaIdFromScan(rawValue);
    return guias.find((item) => {
      if (guiaIdFromQr && Number(item.id) === guiaIdFromQr) return true;
      return [item.numero_guia, item.id].some((candidate) => normalizeGuideLookup(candidate) === normalized);
    });
  };

  const handleScanSubmit = (event) => {
    event.preventDefault();
    if (isFojaEmitted) return;

    const normalized = normalizeGuideLookup(scanInput);
    if (!normalized) {
      setScanFeedback({ tone: 'warning', message: 'Escanea o escribe una guia para agregarla.' });
      scanInputRef.current?.focus();
      return;
    }

    const guide = findGuideByLookup(scanInput);

    if (!guide) {
      setScanFeedback({ tone: 'danger', message: 'La guia no pertenece a la linea elegida, no corresponde al tramo o ya no esta elegible.' });
      setScanInput('');
      scanInputRef.current?.focus();
      return;
    }

    if (selectedIds.includes(guide.id)) {
      setScanFeedback({ tone: 'warning', message: `La guia ${guide.numero_guia || guide.id} ya estaba agregada.` });
      setScanInput('');
      scanInputRef.current?.focus();
      return;
    }

    setSelectedIds((prev) => [...prev, guide.id]);
    setScanFeedback({ tone: 'success', message: `Guia ${guide.numero_guia || guide.id} agregada a la seleccion.` });
    setScanInput('');
    scanInputRef.current?.focus();
  };

  const handleModalScanSubmit = (event) => {
    event.preventDefault();
    if (isFojaEmitted) return;

    const normalized = normalizeGuideLookup(modalScanInput);
    if (!normalized) {
      setModalScanFeedback({ tone: 'warning', message: 'Escanea o escribe una guia valida.' });
      modalScanInputRef.current?.focus();
      return;
    }

    const guide = findGuideByLookup(modalScanInput);

    if (!guide) {
      setModalScanFeedback({ tone: 'danger', message: 'La guia no esta disponible para esta linea o ya no es elegible.' });
      setModalScanInput('');
      modalScanInputRef.current?.focus();
      return;
    }

    if (modalSelectedSet.has(guide.id)) {
      setModalScanFeedback({ tone: 'warning', message: `La guia ${guide.numero_guia || guide.id} ya estaba marcada.` });
      setModalScanInput('');
      modalScanInputRef.current?.focus();
      return;
    }

    setModalSelectedIds((prev) => [...prev, guide.id]);
    setModalScanFeedback({ tone: 'success', message: `Guia ${guide.numero_guia || guide.id} lista para agregar.` });
    setModalScanInput('');
    modalScanInputRef.current?.focus();
  };

  const handleRefreshDetail = () => {
    if (!fojaDetail?.foja?.id) return;
    getFojaDetail(fojaDetail.foja.id)
      .then(setFojaDetail)
      .catch((error) => setErrorMessage(getErrorMessage(error, 'No se pudo actualizar la foja.')));
  };

  const handlePrint = async (fojaId = fojaDetail?.foja?.id) => {
    if (!fojaId) return;
    try {
      await printFojaPdf(fojaId);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'No se pudo generar el PDF de la foja.'));
    }
  };

  const buildEmitPayload = useCallback(
    (overridePayload = {}) => ({
      empresa_id: empresaId,
      linea: selectedLineaCatalog?.id || selectedLinea,
      linea_id: selectedLineaCatalog?.id || selectedLinea,
      sucursal_origen_id: sucursal.id,
      chofer_id: form.chofer_id || null,
      vehiculo_id: form.vehiculo_id || null,
      guia_ids: selectedIds,
      modo_recepcion: form.modo_recepcion,
      auto_confirmar_despacho: canAutoDispatch && Boolean(form.auto_confirmar_despacho),
      precinto_1: form.precinto_1 || null,
      precinto_2: form.precinto_2 || null,
      precinto_3: form.precinto_3 || null,
      observaciones: form.observaciones || null,
      ...overridePayload,
    }),
    [
      canAutoDispatch,
      empresaId,
      form.auto_confirmar_despacho,
      form.chofer_id,
      form.modo_recepcion,
      form.observaciones,
      form.precinto_1,
      form.precinto_2,
      form.precinto_3,
      form.vehiculo_id,
      selectedIds,
      selectedLinea,
      selectedLineaCatalog?.id,
      sucursal.id,
    ],
  );

  const policyAlert = useMemo(() => {
    if (!policyFeedback) return null;
    if (policyFeedback.allowed === false && policyFeedback.error) {
      const details = policyFeedback.error.details || {};
      return {
        tone: policyFeedback.error.code === 'FOJA_OVERRIDE_REQUERIDO' ? 'warning' : 'danger',
        title: policyFeedback.error.code === 'FOJA_OVERRIDE_REQUERIDO' ? 'Límite operativo superado' : 'Regla dura no permitida',
        message: policyFeedback.error.message,
        exceeded: details.exceeded_limits || [],
      };
    }
    if (policyFeedback.allowed !== false && Array.isArray(policyFeedback.warnings) && policyFeedback.warnings.length) {
      return {
        tone: 'warning',
        title: 'Límite operativo superado',
        message: policyFeedback.warning_message || 'La política permite advertir y continuar.',
        exceeded: policyFeedback.warnings,
      };
    }
    return null;
  }, [policyFeedback]);
  const limitStatus = policyAlert
    ? policyAlert.tone === 'danger'
      ? { label: 'Bloqueada por límites', tone: 'danger' }
      : { label: 'Límites con warning', tone: 'warning' }
    : { label: 'Límites OK', tone: 'success' };

  useEffect(() => {
    if (isFojaEmitted || !selectedLinea || !selectedIds.length || !sucursal.id) {
      setPolicyFeedback(null);
      return;
    }

    let active = true;
    prevalidateEmitFoja(buildEmitPayload())
      .then((data) => {
        if (!active) return;
        setPolicyFeedback(data);
      })
      .catch(() => {
        if (!active) return;
        setPolicyFeedback(null);
      });

    return () => {
      active = false;
    };
  }, [
    canAutoDispatch,
    empresaId,
    form.auto_confirmar_despacho,
    form.chofer_id,
    form.modo_recepcion,
    form.observaciones,
    form.precinto_1,
    form.precinto_2,
    form.precinto_3,
    form.vehiculo_id,
    isFojaEmitted,
    buildEmitPayload,
    selectedIds,
    selectedLinea,
    selectedLineaCatalog?.id,
    sucursal.id,
  ]);

  const handleEmit = async ({ printMode = null, overridePayload = {} } = {}) => {
    if (isFojaEmitted) return;
    if (!selectedLinea) {
      setErrorMessage('Debes seleccionar una linea antes de emitir la foja.');
      return;
    }
    if (!selectedIds.length) {
      setErrorMessage('Debes seleccionar al menos una guia.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const detail = await emitFoja(buildEmitPayload(overridePayload));
      setShowEmitModal(false);
      setFojaDetail(detail);
      setShowOverrideModal(false);
      setOverrideReason('');
      setPendingEmitOptions(null);
      const autoDispatched = detail?.foja?.estado === 'despachada_confirmada';
      setSuccessMessage(
        autoDispatched
          ? 'Foja emitida y despachada. Ya figura en viaje y quedo lista para recepcion en destino.'
          : 'Foja emitida correctamente. Quedo cerrada y espera confirmacion de salida.',
      );
      if (detail?.policy_warning?.message) {
        setSuccessMessage((current) => `${current} ${detail.policy_warning.message}`);
      }

      if (printMode) {
        window.setTimeout(() => {
          if (printMode === 'valorizada') {
            setSuccessMessage(
              autoDispatched
                ? 'Foja emitida y despachada. Ya figura en viaje. Se abrió el PDF operativo para imprimir.'
                : 'Foja emitida correctamente. Quedo cerrada y espera confirmacion de salida. Se abrió el PDF operativo para imprimir.',
            );
          }
          void handlePrint(detail?.foja?.id);
        }, 80);
      }
    } catch (error) {
      const payload = error?.payload || error?.response?.data || null;
      if (payload?.code === 'FOJA_OVERRIDE_REQUERIDO') {
        const message = payload.message || payload.error || 'La foja requiere override para continuar.';
        setPolicyFeedback({
          allowed: false,
          error: {
            code: payload.code,
            message,
            details: payload.details || null,
          },
        });
        setPendingEmitOptions({ printMode });
        setShowOverrideModal(true);
        setErrorMessage(message);
        await showError('No se pudo emitir la foja', message);
      } else {
        const message = getEmitFailureMessage(error, 'No se pudo emitir la foja.');
        setErrorMessage(message);
        await showError('No se pudo emitir la foja', message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const primaryAction = useMemo(() => {
    if (isSubmitting) {
      return { label: 'Emitiendo...', disabled: true, onClick: () => {}, title: 'La emision esta en curso.' };
    }
    if (isFojaEmitted) {
      return { label: 'Emitida', disabled: true, onClick: () => {}, title: 'La foja ya fue emitida.' };
    }
    if (!selectedLinea) {
      return {
        label: 'Elegí una línea',
        disabled: true,
        onClick: () => {},
        title: 'Seleccioná una línea para habilitar guías.',
      };
    }
    if (loadingGuias) {
      return { label: 'Cargando guías...', disabled: true, onClick: () => {}, title: 'Buscando guías elegibles para la línea.' };
    }
    if (!safeGuides.length) {
      return {
        label: 'Sin guías',
        disabled: true,
        onClick: () => {},
        title: 'No hay guías elegibles para esta línea.',
      };
    }
    if (!selectedIds.length) {
      return {
        label: 'Emitir foja',
        disabled: true,
        onClick: () => {},
        title: 'Seleccioná guías en el panel operativo.',
      };
    }
    return {
      label: 'Emitir foja',
      disabled: false,
      onClick: () => openEmitModal({ printMode: 'normal' }),
      title: 'Abrir la confirmación de emisión.',
    };
  }, [isFojaEmitted, isSubmitting, loadingGuias, openEmitModal, safeGuides.length, selectedLinea, selectedIds.length]);

  const focusChoferSelect = useCallback(() => {
    window.requestAnimationFrame(() => choferSelectRef.current?.focus?.());
  }, []);

  const focusVehiculoSelect = useCallback(() => {
    window.requestAnimationFrame(() => vehiculoSelectRef.current?.focus?.());
  }, []);

  const focusModeSelect = useCallback(() => {
    window.requestAnimationFrame(() => modoRecepcionRef.current?.focus?.());
  }, []);

  const focusScanInput = useCallback(() => {
    window.requestAnimationFrame(() => scanInputRef.current?.focus?.());
  }, []);

  const editSelectionAction = useMemo(() => {
    if (isFojaEmitted || !selectedLinea || !safeGuides.length || !selectedIds.length) return null;
    return {
      label: 'Editar seleccion',
      disabled: false,
      onClick: openGuidesModal,
    };
  }, [isFojaEmitted, openGuidesModal, safeGuides.length, selectedIds.length, selectedLinea]);

  const confirmEmitFromModal = async () => {
    const emitOptions = pendingEmitOptions || {};
    setShowEmitModal(false);
    await handleEmit(emitOptions);
  };

  useOperationalShortcuts({
    enabled: true,
    isSubmitting,
    isGuidesModalOpen: showGuidesModal,
    isEmitModalOpen: showEmitModal,
    isOverrideModalOpen: showOverrideModal,
    isChoferModalOpen: showChoferModal,
    isVehiculoModalOpen: showVehiculoModal,
    selectedLinea,
    primaryAction,
    onOpenGuides: openGuidesModal,
    onConfirmEmit: confirmEmitFromModal,
    onOpenChoferQuickCreate: () => setShowChoferModal(true),
    onOpenVehiculoQuickCreate: () => setShowVehiculoModal(true),
    onBlockedShortcut: (message) => setErrorMessage(message),
    choferContextRef,
    vehiculoContextRef,
  });

  return (
    <div className="operator-shell flex flex-col gap-2.5 px-3 py-2 text-[rgb(var(--fg))] lg:px-4">
      <section className="hidden operator-glass sticky top-2 z-30 rounded-2xl border border-[rgba(var(--border),0.45)] px-3 py-2 shadow-[0_18px_34px_rgba(var(--shadow),0.14)]">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(var(--border),0.45)] bg-[rgba(var(--subpanel),0.56)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.7)]">
                <ClipboardList size={12} />
                Emision de foja
              </div>
              <h1 className="text-base font-semibold leading-none sm:text-lg">Foja de ruta intersucursal</h1>
              <Badge variant={STATE_VARIANTS[estadoFoja] || 'outline'} className="px-2 py-0.5 text-[9px] tracking-[0.14em]">
                {String(estadoFoja).replaceAll('_', ' ')}
              </Badge>
              <StatusChip label={limitStatus.label} tone={limitStatus.tone} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[rgba(var(--fg),0.68)]">
              <span>Sucursal {sucursal.nombre}</span>
              <span>Línea {selectedLineaCatalog ? `${selectedLineaCatalog.codigo} / ${selectedLineaCatalog.nombre}` : 'pendiente'}</span>
              <span>{formatNumber(activeTotals.guias, 0)} guias</span>
              <span>{formatNumber(activeTotals.m3, 3)} m3</span>
              <span>{modoRecepcionLabel}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="strong"
              size="sm"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              title={primaryAction.title}
            >
              <span className="inline-flex items-center gap-2">
                {primaryAction.label}
                <ShortcutChip>F5</ShortcutChip>
              </span>
            </Button>
            <details className="relative">
              <summary className="flex h-8 cursor-pointer list-none items-center gap-2 rounded-xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.82)] px-3 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.92)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                Mas acciones
                <ChevronDown size={14} />
              </summary>
              <div className="absolute right-0 top-10 min-w-[220px] rounded-2xl border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.98)] p-2 shadow-[0_18px_40px_rgba(var(--shadow),0.22)]">
                <div className="flex flex-col gap-1">
                  <Button size="sm" onClick={primaryAction.onClick} disabled={primaryAction.disabled} title={primaryAction.title}>
                    <span className="inline-flex items-center gap-2">
                      {primaryAction.label}
                      <ShortcutChip>F5</ShortcutChip>
                    </span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => (fojaDetail?.foja?.id ? handleRefreshDetail() : refreshBaseData())} disabled={fojaDetail?.foja?.id ? false : isLoading}>
                    <RefreshCw size={14} />
                    Actualizar
                  </Button>
                  {fojaDetail?.foja?.id ? (
          <div className="mt-2 rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.56)] px-3 py-2 text-[12px]">
            <span className="font-semibold">Foja #{fojaDetail.foja.numero || fojaDetail.foja.id}</span>
            <span>Origen {fojaDetail.foja.sucursal_origen_nombre || fojaDetail.foja.sucursal_origen_id}</span>
            <span>{'->'}</span>
            <span>Destino {fojaDetail.foja.sucursal_destino_nombre || fojaDetail.foja.sucursal_destino_id}</span>
            {fojaDetail.foja.estado === 'cerrada' ? (
              <Link to="/cargas/confirmacion-de-despachos" className="ml-auto rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1.5 font-semibold hover:bg-[rgba(var(--subpanel),0.82)]">
                Ir a despacho
              </Link>
            ) : (
              <Link to={`/fojas/recepcion/${fojaDetail.foja.id}`} className="ml-auto rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1.5 font-semibold hover:bg-[rgba(var(--subpanel),0.82)]">
                Ir a recepcion
              </Link>
            )}
            <Link to="/cargas/fojas-de-ruta" className="rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1.5 font-semibold hover:bg-[rgba(var(--subpanel),0.82)]">
              Consultar fojas
            </Link>
          </div>
        ) : null}
                  <Button variant="outline" size="sm" onClick={() => openEmitModal({ printMode: 'valorizada' })} disabled={isSubmitting || isFojaEmitted || !selectedLinea || !selectedIds.length}>
                    Emitir valorizada
                  </Button>
                </div>
              </div>
            </details>
          </div>
        </div>

        <div className="mt-2 rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.72)] px-3 py-2 text-[13px] font-medium text-[rgba(var(--fg),0.76)]">
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--panel),0.55)] px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.52)]">Línea</div>
              <div className="truncate text-[13px] font-semibold text-[rgb(var(--fg))]">
                {selectedLineaCatalog ? `${selectedLineaCatalog.codigo} · ${selectedLineaCatalog.nombre}` : 'Pendiente'}
              </div>
            </div>
            <div className="rounded-xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--panel),0.55)] px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.52)]">Destino / tramo</div>
              <div className="truncate text-[13px] font-semibold text-[rgb(var(--fg))]">
                {fojaDetail?.foja?.sucursal_destino_nombre || selectedLineaCatalog?.nombre || 'Sin tramo definido'}
              </div>
            </div>
            <div className="rounded-xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--panel),0.55)] px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.52)]">Carga actual</div>
              <div className="text-[13px] font-semibold text-[rgb(var(--fg))]">{guideSummaryBar}</div>
            </div>
            <div className="rounded-xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--panel),0.55)] px-2.5 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.52)]">Estado de límites</div>
              <div className="mt-0.5">
                <StatusChip label={limitStatus.label} tone={limitStatus.tone} />
              </div>
            </div>
          </div>
        </div>

        {fojaDetail?.foja?.id ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.56)] px-3 py-2 text-[12px]">
            <span className="font-semibold">Foja #{fojaDetail.foja.numero || fojaDetail.foja.id}</span>
            <span>Origen {fojaDetail.foja.sucursal_origen_nombre || fojaDetail.foja.sucursal_origen_id}</span>
            <span>{'->'}</span>
            <span>Destino {fojaDetail.foja.sucursal_destino_nombre || fojaDetail.foja.sucursal_destino_id}</span>
            {fojaDetail.foja.estado === 'cerrada' ? (
              <Link to="/cargas/confirmacion-de-despachos" className="ml-auto rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1.5 font-semibold hover:bg-[rgba(var(--subpanel),0.82)]">
                Ir a despacho
              </Link>
            ) : (
              <Link to={`/fojas/recepcion/${fojaDetail.foja.id}`} className="ml-auto rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1.5 font-semibold hover:bg-[rgba(var(--subpanel),0.82)]">
                Ir a recepcion
              </Link>
            )}
            <Link to="/cargas/fojas-de-ruta" className="rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1.5 font-semibold hover:bg-[rgba(var(--subpanel),0.82)]">
              Consultar fojas
            </Link>
          </div>
        ) : null}
      </section>

      {errorMessage ? (
        <div className="rounded-2xl border border-red-400/40 bg-red-500/10 px-3 py-3 text-sm text-red-100" role="alert">
          <div className="font-semibold">No se pudo emitir la foja</div>
          <div className="mt-1">{errorMessage}</div>
        </div>
      ) : null}
      {successMessage ? <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-50">{successMessage}</div> : null}
      {policyAlert ? (
        <div className={`rounded-2xl border px-3 py-3 text-sm ${policyAlert.tone === 'danger' ? 'border-red-400/40 bg-red-500/10 text-red-100' : 'border-amber-400/40 bg-amber-500/10 text-amber-50'}`}>
          <div className="font-semibold">{policyAlert.title}</div>
          <div className="mt-1">{policyAlert.message}</div>
          {policyAlert.exceeded?.length ? (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
              {policyAlert.exceeded.map((item) => <li key={item.key}>{item.message}</li>)}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-3">
        <div className="min-w-0 space-y-2.5">
          <FojaOperationalHeader
            estadoFoja={estadoFoja}
            sucursalNombre={sucursal.nombre}
            activeTotals={activeTotals}
            limitStatus={limitStatus}
            isSubmitting={isSubmitting}
            isFojaEmitted={isFojaEmitted}
            primaryAction={primaryAction}
            onRefreshAction={fojaDetail?.foja?.id ? handleRefreshDetail : refreshBaseData}
            onPrintAction={fojaDetail?.foja?.id ? () => handlePrint() : null}
            onEmitValorizada={() => openEmitModal({ printMode: 'valorizada' })}
            onEditSelection={editSelectionAction}
          />

          <FojaOperationalBar
            lineas={lineaCards}
            selectedLinea={selectedLinea}
            setSelectedLinea={setSelectedLinea}
            selectedLineaCatalog={selectedLineaCatalog}
            guiasLength={guias.length}
            isFojaEmitted={isFojaEmitted}
            loadingGuias={loadingGuias}
            isLoading={isLoading}
            loadingLineasCatalog={loadingLineasCatalog}
            loadingChoferes={loadingChoferes}
            choferOptions={choferOptions}
            vehiculoOptions={vehiculoOptions}
            form={form}
            setForm={setForm}
            formatNumberFn={formatNumber}
            compactControlClass={compactControlClassName}
            lineSelectRef={lineSelectRef}
            choferSelectRef={choferSelectRef}
            vehiculoSelectRef={vehiculoSelectRef}
            modoRecepcionRef={modoRecepcionRef}
            choferContextRef={choferContextRef}
            vehiculoContextRef={vehiculoContextRef}
            onQuickChooseChofer={() => setShowChoferModal(true)}
            onQuickChooseVehiculo={() => setShowVehiculoModal(true)}
            onFocusChofer={focusChoferSelect}
            onFocusVehiculo={focusVehiculoSelect}
            onFocusMode={focusModeSelect}
            onFocusScan={focusScanInput}
          />

          <VehicleLoadGauge
            unidad={selectedVehiculo}
            totals={activeTotals}
            fallbackShape="acoplado"
            layout="row"
            storageKey="foja"
          />

          <FojaGuiasPanel
            selectedLineaCatalog={selectedLineaCatalog}
            activeGuides={activeGuides}
            guias={guias}
            isFojaEmitted={isFojaEmitted}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            guideSummaryBar={guideSummaryBar}
            incidents={incidents}
            timelineItems={timelineItems}
            scanHistory={scanHistory}
            openGuidesModal={openGuidesModal}
            selectedIdsSet={selectedIdsSet}
            toggleSelectedGuide={toggleSelectedGuide}
            formatNumberFn={formatNumber}
            STATE_VARIANTS_MAP={STATE_VARIANTS}
            activeGuidesCount={activeGuides.length}
            checklistItems={checklistItems}
            checklistSummary={checklistSummary}
            isTouchLike={touchLike}
            onChecklistItemFocus={(item) => item.focus?.()}
            scanInput={scanInput}
            setScanInput={setScanInput}
            scanFeedback={scanFeedback}
            setScanFeedback={setScanFeedback}
            handleScanSubmit={handleScanSubmit}
            scanInputRef={scanInputRef}
          />
        </div>
      </div>

      <div className="hidden grid gap-3 xl:grid-cols-[340px_minmax(0,1fr)] 2xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="space-y-3 xl:sticky xl:top-[126px] xl:self-start">
          <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.94)] p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold">Configuracion operativa</h2>
                <p className="mt-1 text-[12px] text-[rgba(var(--fg),0.64)]">Linea, asignaciones y cierre operativo.</p>
              </div>
              <Button
                ref={guidesActionButtonRef}
                type="button"
                variant="strong"
                size="sm"
                onClick={openGuidesModal}
                disabled={!selectedLinea || isFojaEmitted}
                title={!selectedLinea ? 'Seleccioná una línea para habilitar guías.' : 'Abrir selector de guías.'}
              >
                <span className="inline-flex items-center gap-2">
                  {!selectedLinea
                    ? 'Seleccioná una línea para habilitar guías'
                    : safeSelectedIds.length
                      ? `Editar ${formatNumber(safeSelectedIds.length, 0)}`
                      : 'Agregar guías'}
                  <ShortcutChip>F2</ShortcutChip>
                </span>
              </Button>
            </div>

            <div className="mt-3">
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">Lineas</label>
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                {!lineaCards.length ? (
                  <div className="w-full">
                    <EmptyState
                      title="Sin líneas configuradas"
                      description={isLoading || loadingLineasCatalog ? 'Cargando líneas...' : 'No hay líneas operativas disponibles en esta sucursal.'}
                    />
                  </div>
                ) : (
                  lineaCards.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => !isFojaEmitted && setSelectedLinea(String(item.id))}
                      disabled={isFojaEmitted}
                      className={[
                        'min-w-[138px] shrink-0 rounded-2xl border px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                        String(item.id) === String(selectedLinea)
                          ? 'border-[rgba(var(--brand),0.8)] bg-[rgba(var(--brand),0.12)]'
                          : 'border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.75)] hover:bg-[rgba(var(--subpanel),0.85)]',
                      ].join(' ')}
                    >
                      <div className="text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">Línea</div>
                      <div className="mt-0.5 text-sm font-semibold">{item.codigo}</div>
                      <div className="truncate text-[11px] text-[rgba(var(--fg),0.68)]">{item.nombre}</div>
                      <div className="mt-1 text-[11px] text-[rgba(var(--fg),0.58)]">{formatNumber(item.total_guias || 0, 0)} elegibles</div>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="mt-3 rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.7)] p-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Route size={15} />
                Checklist operativo
              </div>
              <div className="mt-2 grid gap-1.5">
                {checklistItems.map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-2 rounded-xl border border-[rgba(var(--border),0.24)] bg-[rgba(var(--panel),0.5)] px-2.5 py-2 text-[13px]">
                    <span>{item.label}</span>
                    <span className="inline-flex items-center gap-1 font-medium">
                      {item.ready ? (
                        <>
                          <CheckCircle2 size={14} className="text-[rgb(var(--brand))]" />
                          <span>OK</span>
                        </>
                      ) : item.warning ? (
                        <>
                          <AlertTriangle size={14} className="text-[rgba(var(--fg),0.7)]" />
                          <span>WARN</span>
                        </>
                      ) : 'Pendiente'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
              <div>
                <label className={`${compactFieldLabelClassName} mb-1.5`}>Modo de recepción</label>
                <Select className={compactControlClassName} value={form.modo_recepcion} onChange={(event) => setForm((prev) => ({ ...prev, modo_recepcion: event.target.value }))} disabled={isFojaEmitted}>
                  {RECEPCION_MODOS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </Select>
              </div>

              <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.64)] p-3">
                <label className="flex cursor-pointer items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">Salida al emitir</p>
                    <p className="mt-1 text-[13px] text-[rgba(var(--fg),0.72)]">
                      {canAutoDispatch
                        ? 'Activalo para que la foja pase directo a en viaje al momento de emitir.'
                        : 'Requiere permiso de confirmar despacho para habilitar salida automatica.'}
                    </p>
                  </div>
                  <Switch
                    id="foja-auto-despacho"
                    checked={Boolean(form.auto_confirmar_despacho)}
                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, auto_confirmar_despacho: checked }))}
                    disabled={isFojaEmitted || !canAutoDispatch}
                    aria-label="Salida automatica al emitir"
                  />
                </label>
              </div>

              <div>
                <div className={compactFieldHeaderClassName}>
                  <label className={compactFieldLabelClassName}>Chofer</label>
                  <Button type="button" variant="ghost" size="sm" className={compactFieldActionClassName} onClick={() => setShowChoferModal(true)} disabled={isFojaEmitted}>
                    <span className="inline-flex items-center gap-2">
                      + Nuevo chofer
                    </span>
                  </Button>
                </div>
                <Select className={compactControlClassName} value={form.chofer_id} onChange={(event) => setForm((prev) => ({ ...prev, chofer_id: event.target.value }))} disabled={isFojaEmitted}>
                  <option value="">{loadingChoferes ? 'Cargando choferes...' : 'Sin asignar'}</option>
                  {choferOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </Select>
              </div>

              <div>
                <div className={compactFieldHeaderClassName}>
                  <label className={compactFieldLabelClassName}>Unidad</label>
                  <Button type="button" variant="ghost" size="sm" className={compactFieldActionClassName} onClick={() => setShowVehiculoModal(true)} disabled={isFojaEmitted}>
                    <span className="inline-flex items-center gap-2">
                      + Nueva unidad
                    </span>
                  </Button>
                </div>
                <Select className={compactControlClassName} value={form.vehiculo_id} onChange={(event) => setForm((prev) => ({ ...prev, vehiculo_id: event.target.value }))} disabled={isFojaEmitted}>
                  <option value="">{isLoading ? 'Cargando unidades...' : 'Sin asignar'}</option>
                  {vehiculoOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </Select>
              </div>
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-3 xl:grid-cols-3">
              <Input className={compactControlClassName} placeholder="Precinto 1" value={form.precinto_1} onChange={(event) => setForm((prev) => ({ ...prev, precinto_1: event.target.value }))} disabled={isFojaEmitted} />
              <Input className={compactControlClassName} placeholder="Precinto 2" value={form.precinto_2} onChange={(event) => setForm((prev) => ({ ...prev, precinto_2: event.target.value }))} disabled={isFojaEmitted} />
              <Input className={compactControlClassName} placeholder="Precinto 3" value={form.precinto_3} onChange={(event) => setForm((prev) => ({ ...prev, precinto_3: event.target.value }))} disabled={isFojaEmitted} />
            </div>

            <div className="mt-3">
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">Observaciones</label>
              <Textarea value={form.observaciones} onChange={(event) => setForm((prev) => ({ ...prev, observaciones: event.target.value }))} placeholder="Observaciones operativas." className={compactTextareaClassName} disabled={isFojaEmitted} />
            </div>

            <div className="mt-3 rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.64)] p-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <PackageSearch size={15} />
                Escaneo rapido
              </div>
              <form onSubmit={handleScanSubmit} className="mt-2 grid gap-2">
                <Input
                  ref={scanInputRef}
                  className={compactControlClassName}
                  value={scanInput}
                  onChange={(event) => {
                    setScanInput(event.target.value);
                    if (scanFeedback) setScanFeedback(null);
                  }}
                  placeholder={selectedLinea ? 'Escanear o ingresar guia' : 'Selecciona una linea primero'}
                  disabled={!selectedLinea || !guias.length || isFojaEmitted}
                />
                <Button type="submit" variant="outline" size="sm" disabled={!selectedLinea || !guias.length || isFojaEmitted}>Agregar por scan</Button>
              </form>
              {scanFeedback ? (
                <div className={`mt-2 rounded-2xl border px-3 py-2 text-sm ${SCAN_FEEDBACK_STYLES[scanFeedback.tone] || SCAN_FEEDBACK_STYLES.warning}`}>
                  {scanFeedback.message}
                </div>
              ) : null}
            </div>
          </section>
        </aside>

        <section className="rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.94)] p-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-sm font-semibold">Panel operativo</h2>
                <p className="mt-1 text-[12px] text-[rgba(var(--fg),0.64)]">
                  {selectedLineaCatalog
                    ? `${activeGuides.length} de ${guias.length || activeGuides.length} guias activas en la linea ${selectedLineaCatalog.codigo || selectedLineaCatalog.id}.`
                    : 'Selecciona una linea para habilitar la operacion.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: 'guias', label: 'Guias' },
                  { id: 'incidencias', label: 'Incidencias' },
                  { id: 'timeline', label: 'Timeline' },
                  { id: 'historial', label: 'Historial' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={[
                      'rounded-xl border px-3 py-1.5 text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                      activeTab === tab.id
                        ? 'border-[rgba(var(--brand),0.8)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]'
                        : 'border-[rgba(var(--border),0.45)] bg-[rgba(var(--panel),0.72)] text-[rgba(var(--fg),0.7)] hover:bg-[rgba(var(--subpanel),0.84)]',
                    ].join(' ')}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'guias' ? (
              <>
                <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.72)] px-3 py-2 text-[12px] text-[rgba(var(--fg),0.7)]">
                  {guideSummaryBar}
                </div>
                <div className="overflow-hidden rounded-2xl border border-[rgba(var(--border),0.32)]">
                  <div className="max-h-[570px] overflow-auto">
                    <table className="min-w-full border-collapse text-sm">
                      <thead className="sticky top-0 z-10 bg-[rgba(var(--subpanel),0.96)] text-left text-[11px] uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">
                        <tr>
                          <th className="px-3 py-3">Guia</th>
                          <th className="px-3 py-3">Cliente</th>
                          <th className="px-3 py-3">Destino</th>
                          <th className="px-3 py-3">Remito</th>
                          <th className="px-3 py-3 text-right">BLTS.</th>
                          <th className="px-3 py-3 text-right">Kg</th>
                          <th className="px-3 py-3 text-right">VD</th>
                          {!isFojaEmitted ? <th className="px-3 py-3 text-right">Acciones</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {!safeActiveGuides.length ? (
                          <tr>
                            <td colSpan={isFojaEmitted ? 7 : 8} className="px-4 py-8 text-center text-[rgba(var(--fg),0.6)]">
                              <div className="p-3">
                                <EmptyState
                                  title={selectedLinea ? 'Todavía no agregaste guías' : 'Elegí una línea'}
                                  description={selectedLinea ? 'Seleccioná guías disponibles o escaneá una guía para cargar la foja.' : 'Elegí una línea para habilitar la selección de guías.'}
                                  action={!isFojaEmitted ? (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={openGuidesModal}
                                      disabled={!selectedLinea}
                                      className="mt-4"
                                    >
                                      <span className="inline-flex items-center gap-2">
                                        {selectedLinea ? 'Agregar guías' : 'Elegí una línea'}
                                        <ShortcutChip>F2</ShortcutChip>
                                      </span>
                                    </Button>
                                  ) : null}
                                />
                              </div>
                            </td>
                          </tr>
                        ) : (
                          safeActiveGuides.map((guia) => (
                            <tr
                              key={guia.id}
                              className={[
                                'border-t border-[rgba(var(--border),0.22)] transition-colors',
                                selectedIdsSet.has(guia.id) ? 'bg-[rgba(var(--brand),0.08)]' : 'hover:bg-[rgba(var(--brand),0.05)]',
                              ].join(' ')}
                            >
                              <td className="px-2 py-2 align-top">
                                <div className="truncate font-semibold text-[rgb(var(--fg))]" title={String(guia.numero_guia || guia.id)}>
                                  {guia.numero_guia || guia.id}
                                </div>
                                <div className="mt-1">
                                  <Badge variant={STATE_VARIANTS[String(guia.estado || '').toLowerCase()] || 'outline'} className="px-2 py-0.5 text-[9px] tracking-[0.14em]">
                                    {String(guia.estado || 'sin estado').replaceAll('_', ' ')}
                                  </Badge>
                                </div>
                              </td>
                              <td className="px-3 py-2.5">{getDestinatarioDisplay(guia).primary || getRemitenteDisplay(guia).primary || DISPLAY_FALLBACK}</td>
                              <td className="px-3 py-2.5">
                                <div className="truncate" title={getDestinoDisplay(guia).title || DISPLAY_FALLBACK}>{getDestinoDisplay(guia).primary || DISPLAY_FALLBACK}</div>
                                {getDestinoDisplay(guia).secondary ? (
                                  <div className="truncate text-[11px] text-[rgba(var(--fg),0.58)]" title={getDestinoDisplay(guia).secondary}>
                                    {getDestinoDisplay(guia).secondary}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-3 py-2.5">{getRemitoDisplay(guia).primary || DISPLAY_FALLBACK}</td>
                              <td className="px-3 py-2.5 text-right">{formatNumber(guia.bultos, 0)}</td>
                              <td className="px-3 py-2.5 text-right">{formatNumber(guia.kg_reales, 2)}</td>
                              <td className="px-3 py-2.5 text-right">{getValorDeclaradoDisplay(guia).primary || DISPLAY_FALLBACK}</td>
                              {!isFojaEmitted ? (
                                <td className="px-3 py-2.5 text-right">
                                  <Button variant="ghost" size="sm" title="Quitar de la foja" aria-label={`Quitar guía ${guia.numero_guia || guia.id} de la foja`} onClick={() => toggleSelectedGuide(guia.id)}>Quitar</Button>
                                </td>
                              ) : null}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : null}

            {activeTab === 'incidencias' ? (
              <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.68)] p-3">
                {!incidents.length ? (
                  <div className="text-sm text-[rgba(var(--fg),0.62)]">Sin incidencias registradas para las guias visibles.</div>
                ) : (
                  <div className="space-y-2">
                    {incidents.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.7)] px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold">{item.numero_guia || item.id}</div>
                          <Badge variant="warning" className="px-2 py-0.5 text-[9px]">{item.incidencia_recepcion || 'Con observacion'}</Badge>
                        </div>
                        <div className="mt-1 text-sm text-[rgba(var(--fg),0.7)]">{item.observaciones_recepcion || 'Sin detalle adicional.'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === 'timeline' ? (
              <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.68)] p-3">
                {!timelineItems.length ? (
                  <div className="text-sm text-[rgba(var(--fg),0.62)]">La timeline se habilita cuando la foja ya tiene eventos persistidos.</div>
                ) : (
                  <div className="space-y-2">
                    {timelineItems.map((item, index) => (
                      <div key={item.id || `${item.fecha || 'item'}-${index}`} className="flex gap-3 rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.72)] px-3 py-2">
                        <TimerReset size={16} className="mt-0.5 shrink-0 text-[rgba(var(--fg),0.55)]" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{item.evento || item.tipo || 'Evento operativo'}</div>
                          <div className="text-[12px] text-[rgba(var(--fg),0.62)]">{item.fecha || item.created_at || item.fecha_evento || 'Sin fecha'}</div>
                          {item.descripcion || item.detalle ? <div className="mt-1 text-sm text-[rgba(var(--fg),0.72)]">{item.descripcion || item.detalle}</div> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === 'historial' ? (
              <div className="rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.68)] p-3">
                {!scanHistory.length ? (
                  <div className="text-sm text-[rgba(var(--fg),0.62)]">Todavia no hay historial de scans o movimientos registrados.</div>
                ) : (
                  <div className="space-y-2">
                    {scanHistory.map((item, index) => (
                      <div key={item.id || `${item.fecha_scan || 'scan'}-${index}`} className="flex gap-3 rounded-2xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.72)] px-3 py-2">
                        <History size={16} className="mt-0.5 shrink-0 text-[rgba(var(--fg),0.55)]" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{item.tipo_scan || 'Movimiento'}</div>
                          <div className="text-[12px] text-[rgba(var(--fg),0.62)]">{item.fecha_scan || item.created_at || 'Sin fecha'}</div>
                          <div className="mt-1 text-sm text-[rgba(var(--fg),0.72)]">
                            {[item.numero_guia, item.resultado, item.observaciones].filter(Boolean).join(' / ') || 'Sin detalle adicional.'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <ModalBase
        isOpen={showGuidesModal}
        onClose={closeGuidesModal}
        labelledBy="modal-guias-foja"
        initialFocusRef={modalScanInputRef}
        className="modal modal-content h-[88vh] w-full max-h-[88vh] overflow-hidden rounded-[18px] border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.985)] p-0 shadow-[0_30px_80px_rgba(var(--shadow),0.34)] text-[12px] focus-visible:outline-none"
        viewportClassName="modal-viewport modal-viewport--wide px-3 py-3 md:px-5"
        backdropClassName="modal-backdrop modal-backdrop--soft"
      >
        <div className="flex h-full min-h-0 flex-col bg-[rgba(var(--panel),0.98)]">
          <div className="border-b border-[rgba(var(--border),0.3)] px-4 py-2.5 md:px-5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[rgba(var(--fg),0.5)]">Armado operativo</div>
                <h3 id="modal-guias-foja" className="mt-0.5 text-[15px] font-semibold text-[rgb(var(--fg))]">Agregar guías</h3>
              </div>
              <div className="text-right text-[11px] text-[rgba(var(--fg),0.58)]">
                <div>{selectedLineaCatalog ? `Línea ${selectedLineaCatalog.codigo || selectedLineaCatalog.id}` : 'Sin línea activa'}</div>
                <div>{formatNumber(filteredGuides.length, 0)} visibles</div>
              </div>
            </div>
          </div>

          <div className="border-b border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.72)] px-4 py-2.5 md:px-5">
            <div className="flex flex-wrap items-center gap-2">
              <form onSubmit={handleModalScanSubmit} className="flex min-w-[260px] flex-[1.2] items-center gap-2">
                <Input
                  ref={modalScanInputRef}
                  value={modalScanInput}
                  onChange={(event) => {
                    setModalScanInput(event.target.value);
                    if (modalScanFeedback) setModalScanFeedback(null);
                  }}
                  className="h-9 rounded-lg text-[12px]"
                  placeholder="Escanear guia"
                  disabled={!selectedLinea || !guias.length || isFojaEmitted}
                />
                <Button type="submit" size="sm" className="h-9 whitespace-nowrap" disabled={!selectedLinea || !guias.length || isFojaEmitted}>
                  Agregar scan
                </Button>
              </form>

              <Input
                value={guideSearch}
                onChange={(event) => setGuideSearch(event.target.value)}
                className="h-9 min-w-[300px] flex-[2.4] rounded-lg text-[12px]"
                placeholder="Buscar por guia, remitente, destinatario o remito"
              />
              <Select value={guideFilters.localidad} className="h-9 min-w-[150px] max-w-[190px] rounded-lg text-[12px]" onChange={(event) => setGuideFilters((prev) => ({ ...prev, localidad: event.target.value }))}>
                <option value="">Localidad</option>
                {localidadOptions.map((item) => <option key={item} value={item}>{item}</option>)}
              </Select>
              <Select value={guideFilters.cliente} className="h-9 min-w-[150px] max-w-[180px] rounded-lg text-[12px]" onChange={(event) => setGuideFilters((prev) => ({ ...prev, cliente: event.target.value }))}>
                <option value="">Cliente</option>
                {clientOptions.map((item) => <option key={item} value={item}>{item}</option>)}
              </Select>
              <Select value={guideFilters.estado} className="h-9 min-w-[120px] max-w-[150px] rounded-lg text-[12px]" onChange={(event) => setGuideFilters((prev) => ({ ...prev, estado: event.target.value }))}>
                <option value="">Estado</option>
                {estadoOptions.map((item) => <option key={item} value={item}>{item}</option>)}
              </Select>
              <Button
                variant="outline"
                size="sm"
                className="h-9 whitespace-nowrap"
                onClick={() => setModalSelectedIds((prev) => Array.from(new Set([...prev, ...filteredGuideIds])))}
                disabled={isFojaEmitted || !filteredGuides.length}
              >
                Marcar visibles
              </Button>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-2xl border border-[rgba(var(--brand),0.22)] bg-[rgba(var(--brand),0.12)] px-3 py-2 text-[15px] font-semibold text-[rgb(var(--fg))]">
                  {getGuideSelectionLabel(modalSelectedIds.length)}
                </div>
                <span className="rounded-full border border-[rgba(var(--border),0.42)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">Guias {formatNumber(modalTotals.guias, 0)}</span>
                <span className="rounded-full border border-[rgba(var(--border),0.42)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">BLTS. {formatNumber(modalTotals.bultos, 0)}</span>
                <span className="rounded-full border border-[rgba(var(--border),0.42)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">Kg {formatNumber(modalTotals.kilos, 2)}</span>
                <span className="rounded-full border border-[rgba(var(--border),0.42)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">M3 {formatNumber(modalTotals.m3, 3)}</span>
                <span className="rounded-full border border-[rgba(var(--border),0.42)] px-3 py-1 text-[11px] font-medium text-[rgba(var(--fg),0.72)]">VD ${formatNumber(modalTotals.valor, 2)}</span>
              </div>
              <div className="text-[11px] text-[rgba(var(--fg),0.58)]">
                Click en la fila para seleccionar. Enter en scan agrega directo.
              </div>
            </div>

            {modalScanFeedback ? (
              <div className={`mt-3 rounded-xl border px-3 py-2 text-[12px] ${SCAN_FEEDBACK_STYLES[modalScanFeedback.tone] || SCAN_FEEDBACK_STYLES.warning}`}>
                {modalScanFeedback.message}
              </div>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[1000px] table-fixed border-collapse text-[12px] leading-5 text-[rgba(var(--fg),0.9)]">
              <colgroup>
                <col className="w-[40px]" />
                <col className="w-[150px]" />
                <col className="w-[180px]" />
                <col className="w-[180px]" />
                <col className="w-[110px]" />
                <col className="w-[140px]" />
                <col className="w-[68px]" />
                <col className="w-[72px]" />
                <col className="w-[96px]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-[rgba(var(--panel),0.99)] text-left text-[10px] uppercase tracking-[0.12em] text-[rgba(var(--fg),0.78)] shadow-[0_1px_0_rgba(var(--border),0.34)]">
                <tr>
                  <th className="px-2 py-2 text-center">SEL</th>
                  <th className="px-2 py-2">GUÍA</th>
                  <th className="px-2 py-2">RTE.</th>
                  <th className="px-2 py-2">DEST.</th>
                  <th className="px-2 py-2">DESTINO</th>
                  <th className="px-2 py-2">REMITO</th>
                  <th className="px-2 py-2 text-right">BLTS.</th>
                  <th className="px-2 py-2 text-right">KG</th>
                  <th className="px-2 py-2 text-right">VD</th>
                </tr>
              </thead>
              <tbody>
                {!filteredGuides.length ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-[12px] text-[rgba(var(--fg),0.72)]">
                      {guias.length ? 'No hay guias para los filtros aplicados.' : 'No hay guias elegibles para la linea seleccionada.'}
                    </td>
                  </tr>
                                ) : (
                  filteredGuides.map((guia) => (
                    <tr
                      key={guia.id}
                      onClick={() => toggleModalGuide(guia.id)}
                      className={[
                        'cursor-pointer border-t border-[rgba(var(--border),0.24)] transition-colors',
                        modalSelectedSet.has(guia.id)
                          ? 'bg-[rgba(var(--brand),0.16)] hover:bg-[rgba(var(--brand),0.2)]'
                          : 'hover:bg-[rgba(var(--fg),0.045)]',
                        isFojaEmitted ? 'cursor-default' : '',
                      ].join(' ')}
                    >
                      <td className="px-2 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={modalSelectedSet.has(guia.id)}
                          onChange={() => toggleModalGuide(guia.id)}
                          onClick={(event) => event.stopPropagation()}
                          className="h-4 w-4 cursor-pointer rounded border-[rgba(var(--border),0.8)] accent-[rgb(var(--brand))]"
                          disabled={isFojaEmitted}
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="truncate font-semibold text-[12px] text-[rgb(var(--fg))]" title={String(guia.numero_guia || guia.id)}>
                          {guia.numero_guia || guia.id}
                        </div>
                        <div className="mt-1">
                          <Badge variant={STATE_VARIANTS[String(guia.estado || '').toLowerCase()] || 'outline'} className="border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.72)] px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] text-[rgba(var(--fg),0.92)]">
                            {String(guia.estado || 'sin estado').replaceAll('_', ' ')}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="min-w-0 truncate text-[12px] text-[rgba(var(--fg),0.9)]" title={guia.remitenteDisplay?.title || DISPLAY_FALLBACK}>{guia.remitenteDisplay?.primary || DISPLAY_FALLBACK}</div>
                        {hasMeaningfulText(guia.clienteDisplay?.primary) && guia.clienteDisplay?.primary !== guia.remitenteDisplay?.primary ? (
                          <div className="hidden min-w-0 truncate text-[10px] text-[rgba(var(--fg),0.72)] lg:block" title={`Pagador: ${guia.clienteDisplay.primary}`}>
                            Pagador: {guia.clienteDisplay.primary}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="min-w-0 truncate text-[12px] text-[rgba(var(--fg),0.9)]" title={guia.destinatarioDisplay?.title || DISPLAY_FALLBACK}>{guia.destinatarioDisplay?.primary || DISPLAY_FALLBACK}</div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="min-w-0 truncate font-medium text-[12px] text-[rgba(var(--fg),0.92)]" title={guia.destinoDisplay?.title || DISPLAY_FALLBACK}>{guia.destinoDisplay?.primary || DISPLAY_FALLBACK}</div>
                        {guia.destinoDisplay?.secondary ? (
                          <div className="min-w-0 truncate text-[10px] text-[rgba(var(--fg),0.72)]" title={guia.destinoDisplay.secondary}>
                            {guia.destinoDisplay.secondary}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="min-w-0 truncate text-[12px] text-[rgba(var(--fg),0.86)]" title={guia.remitoDisplay?.title || DISPLAY_FALLBACK}>{guia.remitoDisplay?.primary || DISPLAY_FALLBACK}</div>
                      </td>
                      <td className="px-2 py-2 text-right align-top tabular-nums text-[rgba(var(--fg),0.82)]">{formatNumber(guia.bultos, 0)}</td>
                      <td className="px-2 py-2 text-right align-top tabular-nums text-[rgba(var(--fg),0.82)]">{formatNumber(guia.kg_reales, 2)}</td>
                      <td className="px-2 py-2 text-right align-top tabular-nums font-medium text-[rgba(var(--fg),0.9)]">{guia.valorDeclaradoDisplay?.primary || DISPLAY_FALLBACK}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t border-[rgba(var(--border),0.32)] bg-[rgba(var(--panel),0.98)] px-4 py-3 md:px-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[15px] font-semibold text-[rgb(var(--fg))]">{getGuideSelectionLabel(modalSelectedIds.length)}</div>
                <div className="text-[11px] text-[rgba(var(--fg),0.58)]">El footer queda fijo para cerrar la seleccion sin perder contexto.</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setModalSelectedIds([])} disabled={isFojaEmitted || !modalSelectedIds.length}>Limpiar</Button>
                <Button variant="outline" onClick={() => setShowGuidesModal(false)}>Cancelar</Button>
                <Button onClick={applySelectedGuides} disabled={isFojaEmitted} className="min-w-[220px]">
                  {`Agregar ${formatNumber(modalSelectedIds.length, 0)} guia${modalSelectedIds.length === 1 ? '' : 's'}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalBase>

      <ModalBase
        isOpen={showOverrideModal}
        onClose={() => {
          setShowOverrideModal(false);
          setPendingEmitOptions(null);
        }}
        labelledBy="modal-override-foja"
        className="modal modal-content w-full max-w-[640px] p-5 md:p-6 focus-visible:outline-none"
      >
        <div className="space-y-4">
          <div>
            <h3 id="modal-override-foja" className="text-lg font-semibold text-[rgb(var(--fg))]">Override de límites operativos</h3>
            <p className="mt-1 text-sm text-[rgba(var(--fg),0.72)]">
              La foja supera límites de carga. Podés continuar solo si la política del tenant lo autoriza.
            </p>
          </div>

          {policyAlert?.exceeded?.length ? (
            <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 px-3 py-3 text-sm text-amber-50">
              <div className="font-semibold">Límites superados</div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                {policyAlert.exceeded.map((item) => <li key={item.key}>{item.message}</li>)}
              </ul>
            </div>
          ) : null}

          <div className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.62)] p-3 text-sm text-[rgba(var(--fg),0.74)]">
            {(policyFeedback?.error?.details?.requires_permission === false || canOverrideLimits)
              ? 'Tu usuario tiene permiso para autorizar el override.'
              : 'Tu usuario no tiene permiso para autorizar este override.'}
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">Motivo</label>
            <Textarea
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
              placeholder={policyFeedback?.error?.details?.requires_motivo === false ? 'Motivo opcional para auditoría.' : 'Explicá por qué se autoriza superar el límite operativo.'}
              className="min-h-[100px] rounded-xl px-3 py-2 text-[13px]"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowOverrideModal(false)}>Cancelar</Button>
            <Button
              onClick={() => handleEmit({
                printMode: pendingEmitOptions?.printMode || null,
                overridePayload: {
                  override_operativo: true,
                  override_motivo: overrideReason.trim() || null,
                },
              })}
              disabled={
                (policyFeedback?.error?.details?.requires_permission !== false && !canOverrideLimits)
                || (policyFeedback?.error?.details?.requires_motivo !== false && !overrideReason.trim())
                || isSubmitting
              }
            >
              {isSubmitting ? 'Autorizando...' : 'Autorizar y emitir'}
            </Button>
          </div>
        </div>
      </ModalBase>

      <EmitFojaModal
        isOpen={showEmitModal}
        onClose={() => {
          setShowEmitModal(false);
          setPendingEmitOptions(null);
        }}
        onConfirm={confirmEmitFromModal}
        form={form}
        setForm={setForm}
        selectedLineaCatalog={selectedLineaCatalog}
        selectedIdsLength={selectedIds.length}
        canAutoDispatch={canAutoDispatch}
        isSubmitting={isSubmitting}
        isFojaEmitted={isFojaEmitted}
        initialFocusRef={observacionesRef}
        observacionesRef={observacionesRef}
        precinto1Ref={precinto1Ref}
        precinto2Ref={precinto2Ref}
        precinto3Ref={precinto3Ref}
      />

      {showChoferModal ? (
        <Suspense fallback={null}>
          <QuickCreateChoferModal
            isOpen={showChoferModal}
            onClose={() => setShowChoferModal(false)}
            empresaId={empresaId}
            sucursalId={sucursal.id}
            onCreated={(chofer) => {
              setQuickChoferes((prev) => [chofer, ...prev.filter((item) => String(item.id) !== String(chofer.id))]);
              setForm((prev) => ({ ...prev, chofer_id: String(chofer.id) }));
            }}
          />
        </Suspense>
      ) : null}

      {showVehiculoModal ? (
        <Suspense fallback={null}>
          <QuickCreateVehiculoModal
            isOpen={showVehiculoModal}
            onClose={() => setShowVehiculoModal(false)}
            sucursalId={sucursal.id}
            onCreated={(vehiculo) => {
              setVehiculos((prev) => [vehiculo, ...prev.filter((item) => String(item.id) !== String(vehiculo.id))]);
              setForm((prev) => ({ ...prev, vehiculo_id: String(vehiculo.id) }));
            }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

export default function EmisionFoja() {
  return (
    <EmisionFojaErrorBoundary>
      <EmisionFojaContent />
    </EmisionFojaErrorBoundary>
  );
}
